import { useState, useCallback, useContext, useRef, useEffect } from 'react';
import { FounderContext, ValidatorContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Mic, MicOff, Loader2, Send, Eye, Volume2, Type, Users, ChevronDown, ChevronUp, Info, AlertCircle, Globe } from 'lucide-react';
import axios from 'axios';
import { markBrowseRegistered } from '@/lib/browseTracker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_FORM = {
  age: '', sex: '', language: 'English',
  patientName: '',
  chiefComplaint: '', symptoms: '', medications: '',
  existingConditions: '', historicalConditions: '', formerExposures: '',
  notes: '', communicationMode: 'text',
  caregiverAssisted: false, consentGiven: false,
  realPatientConsent: false,  // Truth-lock §9.9 — explicit opt-in to count as a verified real-human encounter
  pedWeight: '', pedFeeding: '', pedUrineOutput: '',
  pedVaccinationStatus: '', pedCaregiverRelationship: '',
  contactName: '', contactEmail: '', contactRole: '', contactFeedback: '',
  primaryPhysician: '', specialist: '',
  // Engine 25 — Social Wellbeing (deterministic, no LLM)
  lonelinessAnchor: '',
  ucla3LackCompanionship: '', ucla3FeelLeftOut: '', ucla3FeelIsolated: '',
  bereavementRecent: false,
  // Item 3 — Clinical Validator self-registration (route-driven; server generates token)
  validatorMode: false, validatorName: '', validatorEmail: '', validatorHospital: '', validatorSpecialty: '',
  // Engine 49 — Accessibility (Feb 16, 2026)
  signLanguage: '', interpreterNeeded: false, interpreterType: '',
  caregiverRelationship: '', accessibilityNeeds: [],
  // Engine 50 — Region for disability-resources bundle (empty = auto-detect from language)
  region: '',
  // Surgical History — structured capture (May 17, 2026) for pre-op + anesthesia + surgeon prep
  surgicalHistoryFreeText: '',
  surgicalHistoryRows: [],  // [{procedure, year, facility, complication}]

  // ─── Devarajan Intake v2 — Structured Subjective (May 21, 2026) ──────
  // 6-bundle expansion per Dr. V. N. Devarajan's clinical spec. Lives
  // alongside the legacy `chiefComplaint`/`symptoms` strings — backend
  // shim flattens these into the legacy fields so the 52 engines see
  // byte-identical input. NO LLM interpretation.
  chiefComplaintActiveText: '',      // raw textarea — one line per condition+date
  chiefComplaintInactiveText: '',    // raw textarea — one line per past event+date
  // OLDCARTS 9 sub-fields (kept as flat keys so existing VoiceBtn works)
  sxOnset: '', sxLocation: '', sxDuration: '', sxCharacter: '', sxRadiation: '',
  sxAggravating: '', sxRelieving: '', sxTiming: '', sxSeverity: '',
  sxAssociatedSymptoms: '',
  allergies: '',                     // mandatory on validator surface
  familyHistory: '',

  // ─── Phase B.5 Intake Additions (June 2026) ─────────────────────────
  // Four additive, optional structured fields. Marshalled in handleSubmit and
  // serialized server-side (`_normalize_b5`) into the legacy text strings so
  // the deterministic engines light up on live intake. NO LLM.
  vitalHr: '', vitalRr: '', vitalSpo2: '', vitalSbp: '', vitalDbp: '', vitalTemp: '',
  immunocompromised: false,          // → appends 'immunocompromised' to existingConditions
  recentLabs: '',                    // free-text recent labs
  followUp: {},                      // {questionKey: answer} from conditional CC block
  // ─── PHQ-9 / GAD-7 conditional mood/anxiety screen (July 2026) ──────
  // Item-level responses (0-3 each). Empty array = not administered. Shown
  // only when mood/anxiety is detected or the clinician opts in. Scored
  // server-side into the observational longitudinal tracker (never triage).
  phq9Responses: [],                 // 9 items
  gad7Responses: [],                 // 7 items
};

// ─── PHQ-9 / GAD-7 instruments (deterministic scoring mirrors backend) ──────
const PHQ9_ITEMS = [
  'Little interest or pleasure in doing things',
  'Feeling down, depressed, or hopeless',
  'Trouble falling/staying asleep, or sleeping too much',
  'Feeling tired or having little energy',
  'Poor appetite or overeating',
  'Feeling bad about yourself — that you are a failure or have let yourself/family down',
  'Trouble concentrating on things (reading, watching TV)',
  'Moving/speaking so slowly others noticed — or being so fidgety/restless you moved a lot more',
  'Thoughts that you would be better off dead, or of hurting yourself',
];
const GAD7_ITEMS = [
  'Feeling nervous, anxious, or on edge',
  'Not being able to stop or control worrying',
  'Worrying too much about different things',
  'Trouble relaxing',
  "Being so restless that it's hard to sit still",
  'Becoming easily annoyed or irritable',
  'Feeling afraid, as if something awful might happen',
];
const MOOD_SCALE = [
  { v: 0, label: 'Not at all' },
  { v: 1, label: 'Several days' },
  { v: 2, label: 'More than half the days' },
  { v: 3, label: 'Nearly every day' },
];
// Auto-reveal keywords (specific to limit false positives). Bare "down"/"stress"
// intentionally excluded; "low mood", "stressed", etc. retained.
const MOOD_KEYWORDS = /\b(depress\w*|low mood|mood swing|hopeless|despair|anxi\w*|panic|nervous|worry|worried|worrying|insomnia|can'?t sleep|tearful|suicid\w*|self[-\s]?harm|mental health|ptsd\b|\bocd\b|stressed|distress\w*|dysthymi\w*)\b/i;
const _bandPhq9 = (t) => t <= 4 ? 'minimal' : t <= 9 ? 'mild' : t <= 14 ? 'moderate' : t <= 19 ? 'moderately severe' : 'severe';
const _bandGad7 = (t) => t <= 4 ? 'minimal' : t <= 9 ? 'mild' : t <= 14 ? 'moderate' : 'severe';

// Phase B.5 — Conditional follow-up: deterministic chief-complaint keyword
// clusters (NO LLM). The first matching cluster reveals 3–4 structured
// follow-up questions; answers are serialized into `symptoms` server-side.
const FOLLOWUP_CLUSTERS = [
  {
    id: 'chest_pain',
    match: ['chest pain', 'chest pressure', 'chest tightness', 'chest discomfort', 'angina'],
    title: 'Chest pain — a few quick questions',
    questions: [
      { key: 'cp_radiation', label: 'Does the pain spread anywhere (arm, jaw, back)?', placeholder: 'e.g., radiating to left arm and jaw' },
      { key: 'cp_exertion', label: 'Brought on by exertion and eased by rest?', placeholder: 'e.g., worse on walking, eases with rest' },
      { key: 'cp_assoc', label: 'Any sweating, nausea, or breathlessness with it?', placeholder: 'e.g., sweating and short of breath' },
      { key: 'cp_onset', label: 'When did it start and how long does it last?', placeholder: 'e.g., started 2 hours ago, constant' },
    ],
  },
  {
    id: 'breathing',
    match: ['shortness of breath', 'breathless', 'difficulty breathing', 'wheeze', 'wheezing', 'dyspnea', 'short of breath'],
    title: 'Breathing difficulty — a few quick questions',
    questions: [
      { key: 'br_orthopnea', label: 'Breathless lying flat or waking you at night?', placeholder: 'e.g., wakes me at night, need 3 pillows' },
      { key: 'br_exertion', label: 'How far can you walk before getting breathless?', placeholder: 'e.g., breathless after one flight of stairs' },
      { key: 'br_cough', label: 'Any cough, fever, or coloured phlegm?', placeholder: 'e.g., productive cough with fever' },
      { key: 'br_history', label: 'Any wheeze or known asthma/COPD?', placeholder: 'e.g., wheezing, known COPD' },
    ],
  },
  {
    id: 'abdominal',
    match: ['abdominal pain', 'stomach pain', 'belly pain', 'tummy pain', 'abdo pain', 'abdominal'],
    title: 'Abdominal pain — a few quick questions',
    questions: [
      { key: 'ab_location', label: 'Where exactly is the pain?', placeholder: 'e.g., right lower quadrant' },
      { key: 'ab_food', label: 'Related to meals — better or worse with food?', placeholder: 'e.g., worse after fatty meals' },
      { key: 'ab_bowel', label: 'Any vomiting, diarrhea, or change in bowels?', placeholder: 'e.g., vomiting and no bowel movement' },
      { key: 'ab_redflag', label: 'Any fever or blood in stool/vomit?', placeholder: 'e.g., fever, black stools' },
    ],
  },
  {
    id: 'headache',
    match: ['headache', 'migraine', 'head pain'],
    title: 'Headache — a few quick questions',
    questions: [
      { key: 'ha_onset', label: 'Did it come on suddenly, like a thunderclap?', placeholder: 'e.g., sudden worst-ever headache' },
      { key: 'ha_neuro', label: 'Any weakness, numbness, vision or speech changes?', placeholder: 'e.g., blurred vision, arm weakness' },
      { key: 'ha_meningism', label: 'Any fever, neck stiffness, or rash?', placeholder: 'e.g., fever and stiff neck' },
      { key: 'ha_pattern', label: 'New or different from your usual headaches?', placeholder: 'e.g., new and different, worse in mornings' },
    ],
  },
  {
    id: 'fever',
    match: ['fever', 'febrile', 'high temperature', 'chills', 'rigors'],
    title: 'Fever — a few quick questions',
    questions: [
      { key: 'fv_duration', label: 'How many days of fever, and how high?', placeholder: 'e.g., 3 days, up to 39.5 C' },
      { key: 'fv_localizing', label: 'Any cough, urinary symptoms, rash, or diarrhea?', placeholder: 'e.g., burning urine and back pain' },
      { key: 'fv_travel', label: 'Recent travel or known exposures?', placeholder: 'e.g., returned from a malaria-endemic area' },
      { key: 'fv_immune', label: 'On chemotherapy or immunosuppressed?', placeholder: 'e.g., on chemotherapy' },
    ],
  },
];

function matchFollowUpCluster(chiefComplaint) {
  const cc = (chiefComplaint || '').toLowerCase();
  if (!cc.trim()) return null;
  return FOLLOWUP_CLUSTERS.find(cl => cl.match.some(kw => cc.includes(kw))) || null;
}

// Clinical free-text fields eligible for inline spell-check (module-scope = stable ref).
const SPELL_CHECK_FIELDS = ['chiefComplaint', 'symptoms', 'medications', 'existingConditions'];

// Language map: display name → Sarvam BCP-47 code
const SARVAM_LANG_MAP = {
  'English': 'en-IN', 'Hindi': 'hi-IN', 'Tamil': 'ta-IN', 'Telugu': 'te-IN',
  'Kannada': 'kn-IN', 'Malayalam': 'ml-IN', 'Bengali': 'bn-IN', 'Marathi': 'mr-IN',
  'Gujarati': 'gu-IN', 'Punjabi': 'pa-IN', 'Odia': 'od-IN', 'Assamese': 'as-IN',
  'Urdu': 'ur-IN', 'Nepali': 'ne-IN', 'Konkani': 'kok-IN', 'Kashmiri': 'ks-IN',
  'Sindhi': 'sd-IN', 'Sanskrit': 'sa-IN', 'Santali': 'sat-IN', 'Manipuri': 'mni-IN',
  'Bodo': 'brx-IN', 'Maithili': 'mai-IN', 'Dogri': 'doi-IN',
};

// Text-only UI languages (no Sarvam voice code): labels + consent are hand-coded,
// the mic is hidden and a "text demo" note shown. Full voice planned post-funding.
const TEXT_ONLY_LANGS = ['Mandarin'];

// DPDPA 2023-compliant patient consent. Renders in patient's selected language.
// 11 languages hand-coded for the India clinical validation pilot (May 2026):
//   English · Hindi · Tamil · Telugu · Kannada · Malayalam · Marathi ·
//   Bengali · Gujarati · Punjabi · Urdu
// Remaining 12 Indic languages fall back to English with a small italic note.
// All translations are clinical-grade hand-coded — NOT machine-translated —
// so the consent is defensible under DPDPA 2023.
const CONSENT_TEXT = {
  English: {
    title: 'I consent to data processing for this validation beta',
    body: 'I voluntarily share my health information with P.AI Global Health (Patient AI) for the purpose of evaluating a clinical intake software during its validation phase. (1) This is a validation beta — output is for evaluation only and is not medical advice. (2) I will consult my doctor for all medical decisions. (3) My data is stored securely and will not be sold or shared with advertisers. (4) I can withdraw consent and request deletion of my data at any time by emailing sadasivam.eniasivam@paiglobalhealth.com. (5) No payment is required — the platform is free for patients.',
  },
  Mandarin: {
    title: '我同意在本次验证测试版中处理我的数据',
    body: '我自愿与 P.AI Global Health (Patient AI) 分享我的健康信息，目的是在其验证阶段评估一款临床采集软件。(1) 这是一个验证测试版——输出仅供评估之用，并非医疗建议。(2) 所有医疗决定我都会咨询我的医生。(3) 我的数据会被安全存储，不会出售或与广告商共享。(4) 我可以随时撤回同意，并通过发送电子邮件至 sadasivam.eniasivam@paiglobalhealth.com 请求删除我的数据。(5) 无需付费——本平台对患者免费。',
  },
  Hindi: {
    title: 'मैं इस सत्यापन बीटा के लिए डेटा प्रसंस्करण हेतु सहमति देता/देती हूँ',
    body: 'मैं स्वेच्छा से P.AI Global Health (Patient AI) के साथ अपनी स्वास्थ्य जानकारी साझा करता/करती हूँ, ताकि इसके सत्यापन (validation) चरण के दौरान इस क्लिनिकल इंटेक सॉफ़्टवेयर का मूल्यांकन किया जा सके। (1) यह एक सत्यापन बीटा है — आउटपुट केवल मूल्यांकन के लिए है और चिकित्सीय सलाह नहीं है। (2) सभी चिकित्सीय निर्णयों के लिए मैं अपने डॉक्टर से परामर्श करूँगा/करूँगी। (3) मेरा डेटा सुरक्षित रूप से संग्रहीत है और किसी विज्ञापनदाता को बेचा या साझा नहीं किया जाएगा। (4) मैं कभी भी अपनी सहमति वापस ले सकता/सकती हूँ और sadasivam.eniasivam@paiglobalhealth.com पर ईमेल करके अपने डेटा को हटाने का अनुरोध कर सकता/सकती हूँ। (5) कोई शुल्क नहीं है — यह प्लेटफ़ॉर्म रोगियों के लिए निःशुल्क है।',
  },
  Tamil: {
    title: 'இந்த சரிபார்ப்பு பீட்டாவிற்கான தரவு செயலாக்கத்திற்கு நான் சம்மதிக்கிறேன்',
    body: 'P.AI Global Health (Patient AI) என்ற மருத்துவ உள்ளீட்டு மென்பொருளின் சரிபார்ப்பு (validation) கட்டத்தில் அதை மதிப்பீடு செய்வதற்காக, நான் என் சுய விருப்பத்தின் பேரில் என் சுகாதார தகவலை பகிர்கிறேன். (1) இது ஒரு சரிபார்ப்பு பீட்டா — வெளியீடு மதிப்பீட்டிற்காக மட்டுமே, இது மருத்துவ ஆலோசனை அல்ல. (2) அனைத்து மருத்துவ முடிவுகளுக்கும் நான் என் மருத்துவரை அணுகுவேன். (3) என் தரவு பாதுகாப்பாக சேமிக்கப்படுகிறது; விளம்பரதாரர்களுக்கு விற்கப்படாது, பகிரப்படாது. (4) நான் எந்த நேரத்திலும் என் சம்மதத்தை திரும்பப் பெறலாம், மற்றும் sadasivam.eniasivam@paiglobalhealth.com என்ற முகவரிக்கு மின்னஞ்சல் அனுப்பி என் தரவை அழிக்கக் கோரலாம். (5) எந்த கட்டணமும் கிடையாது — இந்த தளம் நோயாளிகளுக்கு இலவசம்.',
  },
  Telugu: {
    title: 'ఈ ధృవీకరణ బీటా కోసం డేటా ప్రాసెసింగ్‌కు నేను సమ్మతిస్తున్నాను',
    body: 'P.AI Global Health (Patient AI) అనే వైద్య ఇంటేక్ సాఫ్ట్‌వేర్‌ను దాని ధృవీకరణ (validation) దశలో మూల్యాంకనం చేసే ఉద్దేశ్యంతో, నేను స్వచ్ఛందంగా నా ఆరోగ్య సమాచారాన్ని పంచుకుంటున్నాను. (1) ఇది ఒక ధృవీకరణ బీటా — అవుట్‌పుట్ కేవలం మూల్యాంకనం కోసమే, ఇది వైద్య సలహా కాదు. (2) అన్ని వైద్య నిర్ణయాలకు నేను నా వైద్యుడిని సంప్రదిస్తాను. (3) నా డేటా సురక్షితంగా నిల్వ చేయబడుతుంది; ప్రకటనదారులకు అమ్మబడదు లేదా పంచుకోబడదు. (4) నేను ఎప్పుడైనా నా సమ్మతిని ఉపసంహరించుకోవచ్చు మరియు sadasivam.eniasivam@paiglobalhealth.com కు ఇమెయిల్ పంపడం ద్వారా నా డేటాను తొలగించమని అభ్యర్థించవచ్చు. (5) ఎటువంటి చెల్లింపు అవసరం లేదు — ఈ ప్లాట్‌ఫారమ్ రోగులకు ఉచితం.',
  },
  Kannada: {
    title: 'ಈ ಮೌಲ್ಯೀಕರಣ ಬೀಟಾಗಾಗಿ ಡೇಟಾ ಸಂಸ್ಕರಣೆಗೆ ನಾನು ಸಮ್ಮತಿಸುತ್ತೇನೆ',
    body: 'P.AI Global Health (Patient AI) ಎಂಬ ವೈದ್ಯಕೀಯ ಇಂಟೇಕ್ ಸಾಫ್ಟ್‌ವೇರ್ ಅನ್ನು ಅದರ ಮೌಲ್ಯೀಕರಣ (validation) ಹಂತದಲ್ಲಿ ಮೌಲ್ಯಮಾಪನ ಮಾಡುವ ಉದ್ದೇಶದಿಂದ, ನಾನು ಸ್ವಯಂಪ್ರೇರಿತವಾಗಿ ನನ್ನ ಆರೋಗ್ಯ ಮಾಹಿತಿಯನ್ನು ಹಂಚಿಕೊಳ್ಳುತ್ತೇನೆ. (1) ಇದು ಒಂದು ಮೌಲ್ಯೀಕರಣ ಬೀಟಾ — ಔಟ್‌ಪುಟ್ ಕೇವಲ ಮೌಲ್ಯಮಾಪನಕ್ಕಾಗಿ ಮಾತ್ರ, ಇದು ವೈದ್ಯಕೀಯ ಸಲಹೆಯಲ್ಲ. (2) ಎಲ್ಲಾ ವೈದ್ಯಕೀಯ ನಿರ್ಧಾರಗಳಿಗೆ ನಾನು ನನ್ನ ವೈದ್ಯರನ್ನು ಸಂಪರ್ಕಿಸುತ್ತೇನೆ. (3) ನನ್ನ ಡೇಟಾವನ್ನು ಸುರಕ್ಷಿತವಾಗಿ ಸಂಗ್ರಹಿಸಲಾಗುತ್ತದೆ; ಜಾಹೀರಾತುದಾರರಿಗೆ ಮಾರಾಟ ಮಾಡಲಾಗುವುದಿಲ್ಲ ಅಥವಾ ಹಂಚಿಕೊಳ್ಳಲಾಗುವುದಿಲ್ಲ. (4) ನಾನು ಯಾವುದೇ ಸಮಯದಲ್ಲಿ ನನ್ನ ಸಮ್ಮತಿಯನ್ನು ಹಿಂತೆಗೆದುಕೊಳ್ಳಬಹುದು ಮತ್ತು sadasivam.eniasivam@paiglobalhealth.com ಗೆ ಇಮೇಲ್ ಕಳುಹಿಸುವ ಮೂಲಕ ನನ್ನ ಡೇಟಾವನ್ನು ಅಳಿಸಲು ವಿನಂತಿಸಬಹುದು. (5) ಯಾವುದೇ ಪಾವತಿ ಅಗತ್ಯವಿಲ್ಲ — ಈ ವೇದಿಕೆ ರೋಗಿಗಳಿಗೆ ಉಚಿತ.',
  },
  Malayalam: {
    title: 'ഈ വാലിഡേഷൻ ബീറ്റയ്ക്കായി ഡാറ്റ പ്രോസസിംഗിന് ഞാൻ സമ്മതിക്കുന്നു',
    body: 'P.AI Global Health (Patient AI) എന്ന ക്ലിനിക്കൽ ഇൻടേക്ക് സോഫ്റ്റ്‌വെയറിനെ അതിന്റെ വാലിഡേഷൻ (validation) ഘട്ടത്തിൽ വിലയിരുത്തുന്നതിന്, ഞാൻ സ്വമേധയാ എന്റെ ആരോഗ്യ വിവരങ്ങൾ പങ്കിടുന്നു. (1) ഇത് ഒരു വാലിഡേഷൻ ബീറ്റയാണ് — ഔട്ട്‌പുട്ട് വിലയിരുത്തലിന് മാത്രമുള്ളതാണ്, വൈദ്യോപദേശമല്ല. (2) എല്ലാ വൈദ്യ തീരുമാനങ്ങൾക്കും ഞാൻ എന്റെ ഡോക്ടറെ സമീപിക്കും. (3) എന്റെ ഡാറ്റ സുരക്ഷിതമായി സംഭരിക്കപ്പെടും; പരസ്യദാതാക്കൾക്ക് വിൽക്കുകയോ പങ്കിടുകയോ ഇല്ല. (4) ഞാൻ എപ്പോൾ വേണമെങ്കിലും എന്റെ സമ്മതം പിൻവലിക്കാം, sadasivam.eniasivam@paiglobalhealth.com എന്ന വിലാസത്തിലേക്ക് ഇമെയിൽ അയച്ച് എന്റെ ഡാറ്റ നീക്കം ചെയ്യാൻ ആവശ്യപ്പെടാം. (5) ഒരു പണവും ആവശ്യമില്ല — ഈ പ്ലാറ്റ്‌ഫോം രോഗികൾക്ക് സൗജന്യമാണ്.',
  },
  Marathi: {
    title: 'या व्हॅलिडेशन बीटासाठी डेटा प्रक्रियेला माझी संमती आहे',
    body: 'P.AI Global Health (Patient AI) या क्लिनिकल इनटेक सॉफ्टवेअरचे त्याच्या व्हॅलिडेशन (validation) टप्प्यात मूल्यांकन करण्याच्या उद्देशाने, मी स्वेच्छेने माझी आरोग्य माहिती सामायिक करत आहे. (1) हे एक व्हॅलिडेशन बीटा आहे — आउटपुट केवळ मूल्यांकनासाठी आहे आणि वैद्यकीय सल्ला नाही. (2) सर्व वैद्यकीय निर्णयांसाठी मी माझ्या डॉक्टरांचा सल्ला घेईन. (3) माझा डेटा सुरक्षितपणे साठवला जातो; जाहिरातदारांना विकला किंवा सामायिक केला जाणार नाही. (4) मी कधीही माझी संमती मागे घेऊ शकतो/शकते आणि sadasivam.eniasivam@paiglobalhealth.com वर ईमेल पाठवून माझा डेटा हटवण्याची विनंती करू शकतो/शकते. (5) कोणतेही शुल्क आवश्यक नाही — हे प्लॅटफॉर्म रुग्णांसाठी विनामूल्य आहे.',
  },
  Bengali: {
    title: 'এই ভ্যালিডেশন বিটার জন্য ডেটা প্রক্রিয়াকরণে আমি সম্মতি দিচ্ছি',
    body: 'P.AI Global Health (Patient AI) নামক ক্লিনিক্যাল ইনটেক সফটওয়্যারটি তার ভ্যালিডেশন (validation) পর্যায়ে মূল্যায়ন করার উদ্দেশ্যে, আমি স্বেচ্ছায় আমার স্বাস্থ্য তথ্য শেয়ার করছি। (1) এটি একটি ভ্যালিডেশন বিটা — আউটপুট শুধুমাত্র মূল্যায়নের জন্য, এটি চিকিৎসা পরামর্শ নয়। (2) সমস্ত চিকিৎসা সিদ্ধান্তের জন্য আমি আমার ডাক্তারের সাথে পরামর্শ করব। (3) আমার ডেটা নিরাপদে সংরক্ষিত হয়; বিজ্ঞাপনদাতাদের কাছে বিক্রি বা শেয়ার করা হবে না। (4) আমি যে কোনো সময় আমার সম্মতি প্রত্যাহার করতে পারি এবং sadasivam.eniasivam@paiglobalhealth.com-এ ইমেল করে আমার ডেটা মুছে ফেলার অনুরোধ করতে পারি। (5) কোনো অর্থ প্রদানের প্রয়োজন নেই — এই প্ল্যাটফর্মটি রোগীদের জন্য বিনামূল্যে।',
  },
  Gujarati: {
    title: 'આ વેલિડેશન બીટા માટે ડેટા પ્રોસેસિંગ માટે હું સંમતિ આપું છું',
    body: 'P.AI Global Health (Patient AI) નામના ક્લિનિકલ ઇન્ટેક સોફ્ટવેરનું તેના વેલિડેશન (validation) તબક્કા દરમિયાન મૂલ્યાંકન કરવાના હેતુથી, હું સ્વેચ્છાએ મારી આરોગ્ય માહિતી શેર કરું છું. (1) આ એક વેલિડેશન બીટા છે — આઉટપુટ માત્ર મૂલ્યાંકન માટે છે, તે તબીબી સલાહ નથી. (2) તમામ તબીબી નિર્ણયો માટે હું મારા ડૉક્ટરનો સંપર્ક કરીશ. (3) મારો ડેટા સુરક્ષિત રીતે સંગ્રહિત છે; જાહેરાતકર્તાઓને વેચવામાં અથવા શેર કરવામાં આવશે નહીં. (4) હું કોઈપણ સમયે મારી સંમતિ પાછી ખેંચી શકું છું અને sadasivam.eniasivam@paiglobalhealth.com પર ઇમેઇલ કરીને મારો ડેટા કાઢી નાખવાની વિનંતી કરી શકું છું. (5) કોઈ ચુકવણીની જરૂર નથી — આ પ્લેટફોર્મ દર્દીઓ માટે મફત છે.',
  },
  Punjabi: {
    title: 'ਇਸ ਵੈਲੀਡੇਸ਼ਨ ਬੀਟਾ ਲਈ ਡਾਟਾ ਪ੍ਰੋਸੈਸਿੰਗ ਲਈ ਮੈਂ ਸਹਿਮਤੀ ਦਿੰਦਾ/ਦਿੰਦੀ ਹਾਂ',
    body: 'P.AI Global Health (Patient AI) ਨਾਮੀ ਕਲੀਨਿਕਲ ਇਨਟੇਕ ਸੌਫਟਵੇਅਰ ਦਾ ਇਸਦੇ ਵੈਲੀਡੇਸ਼ਨ (validation) ਪੜਾਅ ਦੌਰਾਨ ਮੁਲਾਂਕਣ ਕਰਨ ਦੇ ਉਦੇਸ਼ ਨਾਲ, ਮੈਂ ਆਪਣੀ ਮਰਜ਼ੀ ਨਾਲ ਆਪਣੀ ਸਿਹਤ ਜਾਣਕਾਰੀ ਸਾਂਝੀ ਕਰ ਰਿਹਾ/ਰਹੀ ਹਾਂ। (1) ਇਹ ਇੱਕ ਵੈਲੀਡੇਸ਼ਨ ਬੀਟਾ ਹੈ — ਆਉਟਪੁੱਟ ਸਿਰਫ਼ ਮੁਲਾਂਕਣ ਲਈ ਹੈ, ਇਹ ਡਾਕਟਰੀ ਸਲਾਹ ਨਹੀਂ ਹੈ। (2) ਸਾਰੇ ਡਾਕਟਰੀ ਫੈਸਲਿਆਂ ਲਈ ਮੈਂ ਆਪਣੇ ਡਾਕਟਰ ਨਾਲ ਸਲਾਹ ਕਰਾਂਗਾ/ਕਰਾਂਗੀ। (3) ਮੇਰਾ ਡਾਟਾ ਸੁਰੱਖਿਅਤ ਢੰਗ ਨਾਲ ਸਟੋਰ ਕੀਤਾ ਜਾਂਦਾ ਹੈ; ਇਸ਼ਤਿਹਾਰ ਦੇਣ ਵਾਲਿਆਂ ਨੂੰ ਵੇਚਿਆ ਜਾਂ ਸਾਂਝਾ ਨਹੀਂ ਕੀਤਾ ਜਾਵੇਗਾ। (4) ਮੈਂ ਕਿਸੇ ਵੀ ਸਮੇਂ ਆਪਣੀ ਸਹਿਮਤੀ ਵਾਪਸ ਲੈ ਸਕਦਾ/ਸਕਦੀ ਹਾਂ ਅਤੇ sadasivam.eniasivam@paiglobalhealth.com ਤੇ ਈਮੇਲ ਕਰਕੇ ਆਪਣਾ ਡਾਟਾ ਮਿਟਾਉਣ ਦੀ ਬੇਨਤੀ ਕਰ ਸਕਦਾ/ਸਕਦੀ ਹਾਂ। (5) ਕੋਈ ਭੁਗਤਾਨ ਦੀ ਲੋੜ ਨਹੀਂ — ਇਹ ਪਲੇਟਫਾਰਮ ਮਰੀਜ਼ਾਂ ਲਈ ਮੁਫ਼ਤ ਹੈ।',
  },
  Urdu: {
    title: 'میں اس ویلیڈیشن بیٹا کے لیے ڈیٹا پروسیسنگ کی رضامندی دیتا/دیتی ہوں',
    body: 'P.AI Global Health (Patient AI) نامی کلینیکل انٹیک سافٹ ویئر کا اس کے ویلیڈیشن (validation) مرحلے کے دوران جائزہ لینے کے مقصد سے، میں اپنی مرضی سے اپنی صحت کی معلومات شیئر کر رہا/رہی ہوں۔ (1) یہ ایک ویلیڈیشن بیٹا ہے — آؤٹ پٹ صرف جائزے کے لیے ہے، یہ طبی مشورہ نہیں ہے۔ (2) تمام طبی فیصلوں کے لیے میں اپنے ڈاکٹر سے مشورہ کروں گا/کروں گی۔ (3) میرا ڈیٹا محفوظ طریقے سے ذخیرہ کیا جاتا ہے؛ مشتہرین کو فروخت یا شیئر نہیں کیا جائے گا۔ (4) میں کسی بھی وقت اپنی رضامندی واپس لے سکتا/سکتی ہوں اور sadasivam.eniasivam@paiglobalhealth.com پر ای میل کر کے اپنے ڈیٹا کو حذف کرنے کی درخواست کر سکتا/سکتی ہوں۔ (5) کسی ادائیگی کی ضرورت نہیں — یہ پلیٹ فارم مریضوں کے لیے مفت ہے۔',
  },
};


// ─── Devarajan Intake v2 — Smart date parser + i18n labels (May 21, 2026) ──
// Parser runs client-side on blur. Recognizes:
//   - MM-DD-YY / MM-DD-YYYY  - MM/DD/YY / MM/DD/YYYY
//   - "lifelong" / "since birth" / "always" → LIFELONG
//   - Year only (e.g., "2015")
//   - Month + Year (e.g., "April 2015", "Apr 2015")
const MONTHS_RE = '(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)';
const V2_DATE_PATTERNS = [
  { re: /\b(lifelong|life-long|since birth|always|forever)\b/i, fmt: () => 'LIFELONG' },
  { re: /\b(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})\b/, fmt: (m) => `${m[1]}-${m[2]}-${m[3]}` },
  { re: new RegExp('\\b' + MONTHS_RE + '\\s+(\\d{4})\\b', 'i'), fmt: (m) => `${m[1]} ${m[2]}` },
  { re: /\b(19|20)\d{2}\b/, fmt: (m) => m[0] },
];

function parseV2ConditionLine(line) {
  const raw = (line || '').trim();
  if (!raw) return null;
  for (const { re, fmt } of V2_DATE_PATTERNS) {
    const m = raw.match(re);
    if (m) {
      const date = fmt(m);
      const condition = raw.replace(re, '').replace(/[,.\-\s]+$/, '').trim();
      if (condition) return { condition, date };
    }
  }
  return { condition: raw, date: null };
}

function parseV2Lines(text) {
  return (text || '').split(/\r?\n/).map(parseV2ConditionLine).filter(Boolean);
}

// i18n labels for the 11 new field labels — English / Spanish / Haitian Creole.
// Sarvam voice input still works in any of 22 Indic languages regardless of
// label language: the mic transcribes patient speech and writes English text
// into the field. Only the static label visuals need translation here.
const V2_LABELS = {
  English: {
    cc_section: 'Chief Complaint', cc_subtitle: 'In your own words — what brings you in today?',
    cc_active: 'Active Process — current conditions, ongoing issues, and lifelong allergies',
    cc_active_sub: 'One per line. Type condition then date (e.g., "Chest pain 5-8-26" or "Penicillin allergy lifelong").',
    cc_inactive: 'Inactive — past surgeries, resolved conditions, old fractures',
    cc_inactive_sub: 'One per line. Type event then date (e.g., "Appendectomy 10-1-70").',
    cc_preview: 'Parsed preview (read-only)', cc_no_date: 'no date',
    sx_section: 'Symptoms', sx_subtitle: 'Structured for your doctor (OLDCARTS framework)',
    sx_onset: 'Onset — When did it start?',
    sx_location: 'Location — Where in your body?',
    sx_duration: 'Duration — How long does each episode last?',
    sx_character: 'Character — What does it feel like?',
    sx_radiation: 'Radiation — Does it spread anywhere (e.g., arm, jaw, back, down a leg)?',
    sx_aggravating: 'Aggravating Factors — What makes it worse?',
    sx_relieving: 'Relieving Factors — What makes it better?',
    sx_timing: 'Timing — How often does it happen?',
    sx_severity: 'Severity — On a scale of 1 to 10, how bad?',
    sx_associated: 'Associated Symptoms — Any OTHER symptoms with the main one?',
    meds_label: 'Medications — including OTC, supplements, and sleep aids',
    meds_sub: 'Include dose, frequency, and start date. Over-the-counter medications matter — they can affect your symptoms.',
    allergies_label: 'Allergies',
    allergies_sub: 'Medication, food, and environmental allergies. If none, type "None known". Note: lifelong allergies should also appear in Active Process above.',
    fh_label: 'Family History',
    fh_sub: 'Health conditions in parents, siblings, grandparents.',
  },
  Spanish: {
    cc_section: 'Motivo de Consulta', cc_subtitle: 'En sus propias palabras — ¿qué le trae hoy?',
    cc_active: 'Proceso Activo — condiciones actuales, problemas en curso y alergias permanentes',
    cc_active_sub: 'Uno por línea. Escriba la condición y luego la fecha (ej.: "Dolor de pecho 5-8-26" o "Alergia a penicilina lifelong").',
    cc_inactive: 'Inactivo — cirugías pasadas, condiciones resueltas, fracturas antiguas',
    cc_inactive_sub: 'Uno por línea. Escriba el evento y luego la fecha (ej.: "Apendicectomía 10-1-70").',
    cc_preview: 'Vista previa analizada (sólo lectura)', cc_no_date: 'sin fecha',
    sx_section: 'Síntomas', sx_subtitle: 'Estructurado para su médico (esquema OLDCARTS)',
    sx_onset: 'Inicio — ¿Cuándo comenzó?',
    sx_location: 'Ubicación — ¿En qué parte del cuerpo?',
    sx_duration: 'Duración — ¿Cuánto dura cada episodio?',
    sx_character: 'Características — ¿Cómo se siente?',
    sx_radiation: 'Irradiación — ¿Se extiende a algún lugar (p. ej., brazo, mandíbula, espalda, pierna)?',
    sx_aggravating: 'Factores Agravantes — ¿Qué lo empeora?',
    sx_relieving: 'Factores que Alivian — ¿Qué lo mejora?',
    sx_timing: 'Frecuencia — ¿Con qué frecuencia ocurre?',
    sx_severity: 'Severidad — En una escala del 1 al 10, ¿qué tan grave?',
    sx_associated: 'Síntomas Asociados — ¿Algún OTRO síntoma con el principal?',
    meds_label: 'Medicamentos — incluyendo OTC, suplementos y ayudas para dormir',
    meds_sub: 'Incluya dosis, frecuencia y fecha de inicio. Los medicamentos de venta libre importan — pueden afectar sus síntomas.',
    allergies_label: 'Alergias',
    allergies_sub: 'Alergias a medicamentos, alimentos y ambientales. Si no tiene, escriba "Ninguna conocida". Nota: las alergias permanentes también deben aparecer en Proceso Activo arriba.',
    fh_label: 'Antecedentes Familiares',
    fh_sub: 'Condiciones de salud en padres, hermanos, abuelos.',
  },
  'Haitian Creole': {
    cc_section: 'Plent Prensipal', cc_subtitle: 'Nan pwòp pawòl ou — kisa ki mennen ou jodi a?',
    cc_active: 'Pwosesis Aktif — kondisyon aktyèl, pwoblèm kontinyèl, ak alèji pou tout lavi',
    cc_active_sub: 'Yon pa liy. Tape kondisyon an apre dat la (egz.: "Doulè nan pwatrin 5-8-26" oswa "Alèji penisilin lifelong").',
    cc_inactive: 'Inaktif — operasyon pase, kondisyon ki rezoud, ansyen fraktur',
    cc_inactive_sub: 'Yon pa liy. Tape evènman an apre dat la (egz.: "Apendisektomi 10-1-70").',
    cc_preview: 'Apèsi analize (lekti sèlman)', cc_no_date: 'pa gen dat',
    sx_section: 'Sentòm', sx_subtitle: 'Estriktire pou doktè w (kad OLDCARTS)',
    sx_onset: 'Konmansman — Kilè li te kòmanse?',
    sx_location: 'Kote — Ki kote nan kò w?',
    sx_duration: 'Dire — Konbyen tan chak epizòd dire?',
    sx_character: 'Karaktè — Kijan li santi?',
    sx_radiation: 'Gaye — Èske li gaye yon kote (egz., bra, machwè, do, desann nan janm)?',
    sx_aggravating: 'Faktè Agravan — Kisa ki fè li vin pi mal?',
    sx_relieving: 'Faktè ki Soulaje — Kisa ki fè li vin pi byen?',
    sx_timing: 'Frekans — Konbyen fwa li rive?',
    sx_severity: 'Sevèrite — Sou yon echèl 1 a 10, konbyen li grav?',
    sx_associated: 'Sentòm ki Asosye — Ki LÒT sentòm ak prensipal la?',
    meds_label: 'Medikaman — ki gen ladann san preskripsyon, sipleman, ak èd pou dòmi',
    meds_sub: 'Mete dòz, frekans, ak dat kòmansman. Medikaman san preskripsyon enpòtan — yo ka afekte sentòm ou yo.',
    allergies_label: 'Alèji',
    allergies_sub: 'Alèji medikaman, manje, ak anviwònman. Si pa gen okenn, tape "Pa gen okenn." Nòt: alèji pou tout lavi ta dwe parèt tou nan Pwosesis Aktif anwo a.',
    fh_label: 'Istwa Fanmi',
    fh_sub: 'Kondisyon sante nan paran, frè ak sè, granparan.',
  },
  Mandarin: {
    cc_section: '主诉', cc_subtitle: '用您自己的话——今天是什么原因就诊？',
    cc_active: '活动性问题——目前的疾病、持续存在的问题以及终身过敏',
    cc_active_sub: '每行一项。先写病情再写日期（例如："胸痛 5-8-26" 或 "青霉素过敏 lifelong"）。',
    cc_inactive: '非活动性——既往手术、已痊愈的疾病、陈旧性骨折',
    cc_inactive_sub: '每行一项。先写事件再写日期（例如："阑尾切除术 10-1-70"）。',
    cc_preview: '解析预览（只读）', cc_no_date: '无日期',
    sx_section: '症状', sx_subtitle: '为您的医生结构化整理（OLDCARTS 框架）',
    sx_onset: '发作——何时开始的？',
    sx_location: '部位——在身体的哪个部位？',
    sx_duration: '持续时间——每次发作持续多久？',
    sx_character: '性质——感觉是怎样的？',
    sx_radiation: '放射——是否会扩散到其他部位（例如手臂、下颌、背部、腿部）？',
    sx_aggravating: '加重因素——什么会使其加重？',
    sx_relieving: '缓解因素——什么会使其减轻？',
    sx_timing: '时间规律——多久发作一次？',
    sx_severity: '严重程度——按 1 到 10 分，有多严重？',
    sx_associated: '伴随症状——除主要症状外还有其他症状吗？',
    meds_label: '药物——包括非处方药、保健品和助眠药',
    meds_sub: '请注明剂量、频率和开始日期。非处方药也很重要——它们可能影响您的症状。',
    allergies_label: '过敏史',
    allergies_sub: '药物、食物和环境过敏。如果没有，请填写"无已知过敏"。注意：终身过敏也应同时填写在上方的活动性问题中。',
    fh_label: '家族史',
    fh_sub: '父母、兄弟姐妹、祖父母的健康状况。',
  },
};

function v2Labels(uiLang) {
  return V2_LABELS[uiLang] || V2_LABELS.English;
}


export default function IntakeForm({ onSubmit, isProcessing, onStartNew, editMode = false, editEncounterId = '', initialData = null, onCancelEdit }) {
  const [formData, setFormData] = useState(() => {
    // Seed validator identity from invite-link query params (?name=&email=&specialty=)
    // at initialization so it survives StrictMode double-mount and never flickers.
    const base = { ...INITIAL_FORM };
    if (typeof window !== 'undefined') {
      const p = new URLSearchParams(window.location.search);
      const name = p.get('name'); const email = p.get('email'); const specialty = p.get('specialty');
      if (name) base.validatorName = name;
      if (email) base.validatorEmail = email;
      if (specialty) base.validatorSpecialty = specialty;
    }
    return base;
  });
  const [listeningField, setListeningField] = useState(null);
  const [showAccessibility, setShowAccessibility] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [hasNotifiedNew, setHasNotifiedNew] = useState(false);
  const [isTestCase, setIsTestCase] = useState(false);
  const [showPhysicianWelcome, setShowPhysicianWelcome] = useState(false);
  const [spellCorrections, setSpellCorrections] = useState({}); // { fieldName: [{from,to}, ...] }
  // Feb 2026 Task 2 — Inline validator-mode toggle (no route change required)
  const [localValidatorMode, setLocalValidatorMode] = useState(false);
  // July 2026 — manual opt-in for the PHQ-9/GAD-7 mood/anxiety screen
  const [moodScreenManual, setMoodScreenManual] = useState(false);
  // Guards the reset so it only fires on a genuine edit→new transition,
  // never on mount (StrictMode-safe) — preserves query-param validator prefill.
  const prevEditModeRef = useRef(editMode);
  const { isFounder } = useContext(FounderContext);
  const { isValidator } = useContext(ValidatorContext);
  // validatorBuildStatus state + /api/system/build-status fetch REMOVED
  // 2026-06-08 per truth-lock §9.6 (no engine / pytest / LLM / encounter
  // counts on any public or authenticated page). Admin dashboard retains
  // the counter at /admin/audit-trust.
  // Validator block renders if either the /register/validator route activated it
  // OR the user clicked the inline "I'm a clinical validator" toggle below.
  const showValidatorBlock = isValidator || localValidatorMode;

  // Item 3 — Set validatorMode in form state whenever the route activates validator context
  useEffect(() => {
    if (isValidator) {
      setFormData(prev => ({ ...prev, validatorMode: true }));
    }
  }, [isValidator]);

  // Feedback Link — pre-fill validator identity from query params
  // (?name=&email=&specialty=), so an invited validator's details are ready.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const p = new URLSearchParams(window.location.search);
    const name = p.get('name');
    const email = p.get('email');
    const specialty = p.get('specialty');
    if (name || email || specialty) {
      setFormData(prev => ({
        ...prev,
        ...(name ? { validatorName: name } : {}),
        ...(email ? { validatorEmail: email } : {}),
        ...(specialty ? { validatorSpecialty: specialty } : {}),
      }));
    }
  }, []);

  // Engine 49 — Read URL query string ?ax=sign_language|aac|low_vision|screen_reader|plain_language
  // to pre-fill the intake form when arriving from the /accessibility CTA.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const ax = (params.get('ax') || '').toLowerCase();
    if (!ax) return;

    setFormData(prev => {
      const next = { ...prev };
      const needs = new Set(prev.accessibilityNeeds || []);

      if (ax === 'sign_language' || ax === 'sign-language' || ax === 'asl') {
        next.communicationMode = 'sign_language';
        next.signLanguage = prev.signLanguage || 'ASL';
        next.interpreterNeeded = true;
        next.interpreterType = prev.interpreterType || 'VRI';
      } else if (ax === 'aac' || ax === 'pictogram') {
        next.communicationMode = 'aac';
        needs.add('aac');
      } else if (ax === 'low_vision' || ax === 'large_text') {
        needs.add('large_text');
        needs.add('low_vision');
      } else if (ax === 'screen_reader' || ax === 'blind') {
        needs.add('screen_reader');
        needs.add('blind');
      } else if (ax === 'plain_language' || ax === 'low_literacy') {
        needs.add('plain_language');
        needs.add('low_literacy');
      } else if (ax === 'high_contrast') {
        needs.add('high_contrast');
      }

      next.accessibilityNeeds = Array.from(needs);
      return next;
    });

    // Strip the param so refresh doesn't re-trigger
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('ax');
      window.history.replaceState({}, '', url.toString());
    } catch (e) { /* ignore */ }
  }, []);

  // Lightweight inline spell-check on blur — fires for clinical free-text fields only
  const handleFieldBlur = useCallback(async (fieldName) => {
    if (!SPELL_CHECK_FIELDS.includes(fieldName)) return;
    const text = formData[fieldName] || '';
    if (!text || text.trim().length < 3) {
      setSpellCorrections(prev => ({ ...prev, [fieldName]: [] }));
      return;
    }
    try {
      const res = await axios.post(`${API}/spell-check`, { text });
      if (res.data.count > 0) {
        // Apply corrected text silently to the field + show inline subtext
        setFormData(prev => ({ ...prev, [fieldName]: res.data.corrected }));
        setSpellCorrections(prev => ({ ...prev, [fieldName]: res.data.corrections }));
      } else {
        setSpellCorrections(prev => ({ ...prev, [fieldName]: [] }));
      }
    } catch (_) {
      // Fail silent — never block submission on spell-check failure
    }
  }, [formData]);

  // Pre-fill form when edit mode initialData is provided
  useEffect(() => {
    if (editMode && initialData) {
      // Extract pediatric fields out of notes if present
      setFormData({
        ...INITIAL_FORM,
        ...initialData,
        consentGiven: true, // consent is always true for edit (already given originally)
      });
    } else if (!editMode && prevEditModeRef.current === true) {
      // Only reset on a genuine edit→new transition, never on first mount —
      // the mount reset previously wiped the query-param validator prefill.
      setFormData(INITIAL_FORM);
    }
    prevEditModeRef.current = editMode;
  }, [editMode, initialData]);

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear inline spell-correction subtext as soon as user edits the field again
    setSpellCorrections(prev => prev[field] ? { ...prev, [field]: [] } : prev);
    if (!hasNotifiedNew && onStartNew) {
      onStartNew();
      setHasNotifiedNew(true);
    }
  }, [hasNotifiedNew, onStartNew]);

  // July 2026 — set a single PHQ-9/GAD-7 item response (0-3). Backfills the
  // array to full length with nulls so partial answers are trackable.
  const setMoodItem = useCallback((instrument, idx, value) => {
    setFormData(prev => {
      const key = instrument === 'phq9' ? 'phq9Responses' : 'gad7Responses';
      const len = instrument === 'phq9' ? 9 : 7;
      const base = (Array.isArray(prev[key]) && prev[key].length === len)
        ? [...prev[key]] : Array(len).fill(null);
      base[idx] = value;
      return { ...prev, [key]: base };
    });
  }, []);

  const [voiceError, setVoiceError] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [sttProcessing, setSttProcessing] = useState(null); // field name being processed
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Sarvam AI Voice: Record audio → send to /api/sarvam/stt → fill field
  const startSarvamVoice = useCallback((fieldName) => {
    setVoiceError(null);
    setSttProcessing(null);

    if (isRecording && listeningField === fieldName) {
      // Stop recording
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      setListeningField(null);
      setIsRecording(false);
      return;
    }

    // Start recording via MediaRecorder
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(stream => {
        const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];
        setListeningField(fieldName);
        setIsRecording(true);

        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) audioChunksRef.current.push(e.data);
        };

        mediaRecorder.onstop = async () => {
          stream.getTracks().forEach(t => t.stop());
          setIsRecording(false);
          setListeningField(null);

          const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          if (blob.size < 500) {
            setVoiceError('Recording too short. Please speak for at least 1-2 seconds.');
            return;
          }

          setSttProcessing(fieldName);
          const langCode = SARVAM_LANG_MAP[formData.language] || 'en-IN';
          const formPayload = new FormData();
          formPayload.append('file', blob, 'recording.webm');
          formPayload.append('language_code', langCode);
          formPayload.append('mode', 'transcribe');

          try {
            const res = await fetch(`${API}/sarvam/stt`, { method: 'POST', body: formPayload });
            const result = await res.json();
            if (result.transcript) {
              setFormData(prev => ({
                ...prev,
                [fieldName]: prev[fieldName] ? prev[fieldName] + ' ' + result.transcript : result.transcript,
              }));
              setVoiceError(null);
            } else {
              setVoiceError(result.error || 'No speech recognized. Please try again.');
            }
          } catch (err) {
            setVoiceError('Failed to transcribe audio. Check your connection and try again.');
          }
          setSttProcessing(null);
        };

        mediaRecorder.start();
        // Auto-stop after 25 seconds (Sarvam REST limit ~30s)
        setTimeout(() => {
          if (mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
          }
        }, 25000);
      })
      .catch(err => {
        if (err.name === 'NotAllowedError') {
          setVoiceError('Microphone access is blocked. Tap the lock icon next to the URL → set Microphone to "Allow" → try again.');
        } else {
          setVoiceError('Could not access microphone: ' + err.message);
        }
      });
  }, [formData.language, isRecording, listeningField]);

  const handleSubmit = useCallback((viewMode) => {
    if (!formData.consentGiven) {
      alert('Please provide consent before submitting.');
      return;
    }
    if (!formData.chiefComplaint.trim()) {
      alert('Please enter your chief complaint.');
      return;
    }
    // Clinical Validator gate (May 19, 2026) — name + email mandatory when validator block is showing.
    // Hospital affiliation remains optional. Founder uses this to attribute feedback to a specific clinician.
    const validatorActive = !!isValidator || !!localValidatorMode;
    if (validatorActive) {
      if (!formData.validatorName.trim() || !formData.validatorEmail.trim()) {
        alert('Clinical Validator Registration requires your name and email so the founder knows whose feedback this is.');
        return;
      }
    }
    // ─── Devarajan v2 — Allergies is mandatory on every intake (May 21, 2026) ──
    // Allergies are a critical safety field. Accept "None known" as a valid answer
    // but never let the form submit with an empty Allergies textarea.
    if (!(formData.allergies || '').trim()) {
      alert('Allergies is a required field. If you have none, please type "None known".');
      return;
    }
    // Serialize pediatric fields into notes for backend compatibility
    const payload = { ...formData };

    // ─── Devarajan v2 — Marshal structured Subjective into payload ────────
    // Engines still consume the legacy `chiefComplaint`/`symptoms` strings —
    // the backend `_normalize_intake_v2` shim flattens these structured shapes
    // back into the legacy strings. We send both shapes so the SOAP renderer
    // and admin views can display the structured detail when present.
    payload.chiefComplaintActiveRows = parseV2Lines(formData.chiefComplaintActiveText);
    payload.chiefComplaintInactiveRows = parseV2Lines(formData.chiefComplaintInactiveText);
    payload.symptomsStructured = {
      onset: formData.sxOnset || '',
      location: formData.sxLocation || '',
      duration: formData.sxDuration || '',
      character: formData.sxCharacter || '',
      radiation: formData.sxRadiation || '',
      aggravating: formData.sxAggravating || '',
      relieving: formData.sxRelieving || '',
      timing: formData.sxTiming || '',
      severity: formData.sxSeverity || '',
      associated_symptoms: formData.sxAssociatedSymptoms || '',
      legacy_notes: formData.symptoms || '',
    };
    // Strip the flat OLDCARTS sub-field keys from the wire — server only needs the structured object.
    delete payload.sxOnset; delete payload.sxLocation; delete payload.sxDuration;
    delete payload.sxCharacter; delete payload.sxAggravating; delete payload.sxRelieving;
    delete payload.sxRadiation;
    delete payload.sxTiming; delete payload.sxSeverity; delete payload.sxAssociatedSymptoms;

    // ─── Phase B.5 — Marshal vitals + follow-up into the structured shapes ──
    // the backend `_normalize_b5` shim serializes into legacy text strings.
    payload.vitals = {
      hr: formData.vitalHr || '', rr: formData.vitalRr || '', spo2: formData.vitalSpo2 || '',
      sbp: formData.vitalSbp || '', dbp: formData.vitalDbp || '', temp: formData.vitalTemp || '',
    };
    delete payload.vitalHr; delete payload.vitalRr; delete payload.vitalSpo2;
    delete payload.vitalSbp; delete payload.vitalDbp; delete payload.vitalTemp;
    // Only ship follow-up answers for the cluster that currently matches the CC.
    const _activeCluster = matchFollowUpCluster(formData.chiefComplaint);
    const _activeKeys = _activeCluster ? _activeCluster.questions.map(q => q.key) : [];
    payload.followUpResponses = Object.fromEntries(
      Object.entries(formData.followUp || {}).filter(([k, v]) => _activeKeys.includes(k) && String(v).trim())
    );
    delete payload.followUp;
    // immunocompromised (bool) and recentLabs (string) ship as-is.

    const pedParts = [];
    if (payload.pedWeight) pedParts.push(`Weight: ${payload.pedWeight}`);
    if (payload.pedFeeding) pedParts.push(`Feeding/Oral Intake: ${payload.pedFeeding}`);
    if (payload.pedUrineOutput) pedParts.push(`Urine Output: ${payload.pedUrineOutput}`);
    if (payload.pedVaccinationStatus) pedParts.push(`Vaccination: ${payload.pedVaccinationStatus}`);
    if (payload.pedCaregiverRelationship) pedParts.push(`Caregiver: ${payload.pedCaregiverRelationship}`);
    if (pedParts.length > 0) {
      const pedNote = '[Pediatric: ' + pedParts.join(', ') + ']';
      payload.notes = payload.notes ? payload.notes + ' ' + pedNote : pedNote;
      // Also add feeding/urine to symptoms for risk detection
      if (payload.pedFeeding === 'poor' || payload.pedFeeding === 'none') {
        payload.symptoms = payload.symptoms ? payload.symptoms + ', poor oral intake' : 'poor oral intake';
      }
      if (payload.pedUrineOutput === 'decreased' || payload.pedUrineOutput === 'minimal') {
        payload.symptoms = payload.symptoms ? payload.symptoms + ', decreased urine output' : 'decreased urine output';
      }
    }
    // Remove frontend-only fields before sending
    delete payload.pedWeight; delete payload.pedFeeding; delete payload.pedUrineOutput;
    delete payload.pedVaccinationStatus; delete payload.pedCaregiverRelationship;

    // Serialize Surgical History into notes for engines that don't yet read structured fields.
    // The structured fields ALSO ship raw so Engine 35 (pre-op risk) and the physician view can consume them.
    const sxStructured = (payload.surgicalHistoryRows || []).filter(r => (r.procedure || '').trim());
    if (sxStructured.length > 0 || (payload.surgicalHistoryFreeText || '').trim()) {
      const sxParts = [];
      sxStructured.forEach(r => {
        const seg = [r.procedure, r.year, r.facility, r.complication].filter(Boolean).join(' / ');
        if (seg) sxParts.push(seg);
      });
      if ((payload.surgicalHistoryFreeText || '').trim()) sxParts.push(payload.surgicalHistoryFreeText.trim());
      const sxNote = '[Surgical History: ' + sxParts.join('; ') + ']';
      payload.notes = payload.notes ? payload.notes + ' ' + sxNote : sxNote;
    }

    if (isTestCase) payload.notes = '[SYSTEM_TEST] ' + (payload.notes || '');
    payload.viewMode = viewMode;  // backend uses this for Rule D physician decision gate
    // Item 3 — Override validatorMode from React context OR inline toggle (survives form resets)
    payload.validatorMode = !!isValidator || !!localValidatorMode;
    // ─── PHQ-9 / GAD-7 — ship only FULLY-answered screens (July 2026) ─────
    // Partial or unadministered screens are stripped so the backend never
    // receives nulls. Scored server-side into the observational tracker only.
    const _moodText = `${formData.chiefComplaint} ${formData.symptoms} ${formData.notes} ${formData.existingConditions} ${formData.historicalConditions}`;
    const _moodOn = MOOD_KEYWORDS.test(_moodText) || moodScreenManual;
    const _phq = formData.phq9Responses;
    if (_moodOn && Array.isArray(_phq) && _phq.length === 9 && _phq.every(v => v !== null && v !== undefined)) {
      payload.phq9Responses = _phq.map(Number);
    } else { delete payload.phq9Responses; }
    const _gad = formData.gad7Responses;
    if (_moodOn && Array.isArray(_gad) && _gad.length === 7 && _gad.every(v => v !== null && v !== undefined)) {
      payload.gad7Responses = _gad.map(Number);
    } else { delete payload.gad7Responses; }
    // Truth-lock §9.9 — only an explicit opt-in counts as a verified real-human
    // encounter. System-test and validator traffic never counts.
    payload.realPatientConsent = !!formData.realPatientConsent && !isTestCase && !payload.validatorMode;
    onSubmit(payload, viewMode);
    // Task 4 — flip this visitor's anonymous browse session to "converted" on explicit registration.
    if (payload.realPatientConsent) { try { markBrowseRegistered(); } catch (_) { /* no-op */ } }
    if (!editMode) {
      setFormData(INITIAL_FORM);
      setHasNotifiedNew(false);
      setIsTestCase(false);
      setMoodScreenManual(false);
    }
  }, [formData, onSubmit, isTestCase, editMode, isValidator, localValidatorMode, moodScreenManual]);

  const VoiceBtn = ({ field }) => {
    if (TEXT_ONLY_LANGS.includes(formData.language)) return null;
    const isActive = listeningField === field && isRecording;
    const isProcessingStt = sttProcessing === field;
    return (
      <button
        type="button"
        onClick={() => sarvamVoiceEnabled ? startSarvamVoice(field) : null}
        disabled={isProcessingStt}
        className={`absolute right-2 top-2 p-1.5 rounded-lg transition-colors ${isProcessingStt ? 'bg-blue-100 text-blue-600' : isActive ? 'bg-red-100 text-red-600 animate-pulse-soft' : 'bg-slate-100 text-slate-500 hover:bg-sky-100 hover:text-[#0369A1]'}`}
        data-testid={`voice-btn-${field}`}
        title={isProcessingStt ? 'Transcribing...' : isActive ? 'Recording... tap to stop' : `Voice input (${formData.language})`}
      >
        {isProcessingStt ? <Loader2 className="w-4 h-4 animate-spin" /> : isActive ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
      </button>
    );
  };

  const sarvamVoiceEnabled = true; // Sarvam AI powered

  const now = new Date();

  return (
    <section className="px-6 py-12" id="intake-section" data-testid="intake-section">
      <div className="text-center mb-6 max-w-5xl mx-auto">
        <Badge className="bg-[#2563EB] text-white mb-3">Step 1</Badge>
        <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-slate-900 mb-2">Tell Us About Your Health</h2>
        <p className="text-sm sm:text-base text-slate-600">Enter your health information below. Use voice input or type — your summary is generated automatically.</p>
      </div>

      {/* Encounter ID Note */}
      <div className="max-w-3xl mx-auto">
        {editMode && (
          <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4 mb-4 flex items-center justify-between shadow-sm" data-testid="edit-mode-banner">
            <div>
              <p className="text-sm font-bold text-amber-900">Editing encounter: <span className="font-mono">{editEncounterId}</span></p>
              <p className="text-xs text-amber-700 mt-0.5">Save to re-run the pipeline and update this record in place. Counter will NOT change.</p>
            </div>
            {onCancelEdit && (
              <Button onClick={onCancelEdit} variant="outline" size="sm" className="border-amber-400 text-amber-900 hover:bg-amber-100" data-testid="cancel-edit-btn">
                Cancel Edit
              </Button>
            )}
          </div>
        )}
        <div className="bg-sky-50 border-2 border-sky-300 rounded-xl p-4 mb-6 text-center shadow-sm" data-testid="encounter-id-note">
          <p className="text-sm sm:text-base text-sky-900 font-bold">To retrieve your history, please note your Encounter ID and keep it safe.</p>
        </div>

        {/* Item 3 — Clinical Validator Registration banner + name/email
            (route /register/validator OR inline "I'm a validator" toggle) */}
        {showValidatorBlock && (
          <details
            open
            className="bg-white border-2 border-slate-300 rounded-xl p-5 mb-4 shadow-sm group"
            data-testid="validator-founder-letter"
          >
            <summary className="cursor-pointer list-none flex items-center justify-between">
              <h3 className="text-base font-bold text-slate-900">
                A letter from the founder &mdash; for clinical validators
              </h3>
              <span className="text-xs text-slate-500 group-open:hidden">Tap to expand</span>
              <span className="text-xs text-slate-500 hidden group-open:inline">Tap to collapse</span>
            </summary>
            <div className="mt-4 space-y-3 text-sm text-slate-800 leading-relaxed">
              <p>
                <strong>P.AI Global Health (Patient AI) does not interpret the patient&rsquo;s history. It assembles it.</strong>{' '}
                The patient brings together records from disparate sources &mdash; primary care, specialists, ER visits,
                foreign hospitals, pharmacies &mdash; that have likely never lived together inside any single EMR or EHR
                your practice can see. Under the <strong>21st Century Cures Act</strong>, the patient has the right to
                consolidate that record across providers. P.AI is where they do it.
              </p>
              <p>
                The output is a structured clinical brief in a <strong>SOAP format physicians already know how to read</strong>.
                It surfaces past clinical flags from sources your system never had access to &mdash; old allergies,
                prior surgical complications, medication interactions, missed screenings &mdash; so you can spend the
                visit on the chief complaint instead of reconstructing history.
              </p>
              <p>
                You choose the view: a chronological past-history timeline alone, or past history plus a draft SOAP
                intended for your review and sign-off. <strong>Nothing is invented. No large language model touches
                the clinical decision layer.</strong> The physician remains the sole diagnostic decision-maker.
              </p>
              <p className="bg-amber-50 border-l-4 border-amber-500 px-3 py-2 text-amber-900">
                <strong>Sandbox notice.</strong> <strong>Not HIPAA-compliant.</strong> This is a synthetic-data sandbox.{' '}
                The production architecture is designed to be <strong>HIPAA-Eligible</strong>.
              </p>
              {/* validator-letter-live-build-status block REMOVED 2026-06-08
                  per truth-lock §9.6: no engine count, no pytest count, no LLM
                  count, no encounter count is surfaced on any public or
                  authenticated page (including the validator letter). The
                  admin dashboard at /admin/audit-trust retains the counter;
                  nothing else surfaces these numbers anywhere. */}
              <div className="pt-2 border-t border-slate-200">
                <p className="text-xs text-slate-500 mt-2 italic">
                  &mdash; Sadasivam Eniasivam, Founder &middot; P.AI Global Health
                </p>
              </div>
            </div>
          </details>
        )}

        {showValidatorBlock && (
          <div className="bg-emerald-50 border-2 border-emerald-400 rounded-xl p-5 mb-6 shadow-sm" data-testid="validator-registration-block">
            <h3 className="text-base font-bold text-emerald-900 mb-1">Clinical Validator Registration <span className="text-red-600">*</span></h3>
            <p className="text-sm text-emerald-800 mb-3">
              Please enter your name and email so the founder, <strong>Sadasivam Eniasivam</strong>, knows whose
              feedback this is. He is reaching out to credible clinical validators across multiple specialties
              to confirm that the intake → SOAP renders deterministically — and to refine the platform based
              on your ideas. Your hospital affiliation is optional but helpful for context.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="validatorName" className="text-sm font-medium text-slate-800">
                  Your name <span className="text-red-600">*</span>
                </Label>
                <Input
                  id="validatorName"
                  value={formData.validatorName}
                  onChange={e => updateField('validatorName', e.target.value)}
                  placeholder="Dr. Jane Smith"
                  data-testid="validator-name-input"
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="validatorEmail" className="text-sm font-medium text-slate-800">
                  Your email <span className="text-red-600">*</span>
                </Label>
                <Input
                  id="validatorEmail"
                  type="email"
                  value={formData.validatorEmail}
                  onChange={e => updateField('validatorEmail', e.target.value)}
                  placeholder="jane.smith@hospital.org"
                  data-testid="validator-email-input"
                  className="mt-1"
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="validatorHospital" className="text-sm font-medium text-slate-800">
                  Hospital / clinic affiliation <span className="text-slate-500 font-normal">(optional)</span>
                </Label>
                <Input
                  id="validatorHospital"
                  value={formData.validatorHospital}
                  onChange={e => updateField('validatorHospital', e.target.value)}
                  placeholder="e.g., Mass General · Apollo Hospitals · Independent practice"
                  data-testid="validator-hospital-input"
                  className="mt-1"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="validatorSpecialty" className="text-sm font-medium text-slate-800">
                  Your primary specialty <span className="text-slate-500 font-normal">(optional — helps group feedback as the cohort grows)</span>
                </Label>
                <Input
                  id="validatorSpecialty"
                  value={formData.validatorSpecialty}
                  onChange={e => updateField('validatorSpecialty', e.target.value)}
                  placeholder="e.g., Internal Medicine · Cardiology · Emergency Medicine"
                  data-testid="validator-specialty-input"
                  className="mt-1"
                />
              </div>
            </div>
            {/* Inline cancel button — only shows when user toggled locally
                (the route-driven /register/validator path keeps the block sticky) */}
            {!isValidator && localValidatorMode && (
              <button
                type="button"
                onClick={() => {
                  setLocalValidatorMode(false);
                  setFormData(prev => ({ ...prev, validatorName: '', validatorEmail: '', validatorHospital: '' }));
                }}
                className="mt-3 text-xs text-emerald-700 hover:text-emerald-900 underline"
                data-testid="validator-toggle-off-btn"
              >
                Cancel — I'm not a validator
              </button>
            )}
          </div>
        )}

        {/* Inline "I'm a clinical validator" toggle — Feb 2026 Task 2 fix.
            Lets a clinician self-promote to validator mode without changing URLs. */}
        {!showValidatorBlock && (
          <div className="flex items-center justify-end mb-4" data-testid="validator-toggle-row">
            <button
              type="button"
              onClick={() => setLocalValidatorMode(true)}
              className="text-sm sm:text-base text-emerald-700 hover:text-emerald-900 underline font-semibold"
              data-testid="validator-toggle-on-btn"
            >
              I&apos;m a clinical validator — register here
            </button>
          </div>
        )}

      {/* Accessibility Block */}
      <div className="intake-card mb-4 p-4" data-testid="accessibility-block">
        <button onClick={() => setShowAccessibility(!showAccessibility)} className="flex items-center justify-between w-full text-left">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#0369A1]" />
            <span className="font-medium text-sm text-slate-800">Accessibility Support Available</span>
          </div>
          {showAccessibility ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>
        {showAccessibility && (
          <div className="mt-3 text-sm text-slate-600 space-y-2 pl-6" data-testid="accessibility-details">
            <p>If you have difficulty seeing, use Listen, Large Text, or High Contrast.</p>
            <p>If you have difficulty hearing, all steps can be completed in text.</p>
            <p>Voice input is optional — typing works for everything.</p>
            <p>If someone is helping you, caregiver-assisted intake is available.</p>
            <p className="text-xs text-slate-500 italic">Designed to support all patients — including those with vision or hearing challenges.</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {[
                { icon: <Volume2 className="w-3 h-3" />, label: 'Screen Reader' },
                { icon: <Type className="w-3 h-3" />, label: 'Large Text' },
                { icon: <Eye className="w-3 h-3" />, label: 'Hearing' },
                { icon: <Users className="w-3 h-3" />, label: 'Caregiver Support' },
              ].map((opt, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-sky-50 text-[#0369A1] border border-sky-100">{opt.icon}{opt.label}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* How to Complete Guide */}
      <div className="intake-card mb-6 p-4" data-testid="how-to-guide">
        <button onClick={() => setShowGuide(!showGuide)} className="flex items-center justify-between w-full text-left">
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4 text-[#0369A1]" />
            <span className="font-medium text-sm text-slate-800">How to Complete Your Intake</span>
          </div>
          {showGuide ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>
        {showGuide && (
          <ol className="mt-3 text-sm text-slate-600 space-y-1 pl-6 list-decimal" data-testid="guide-details">
            <li>Encounter ID is auto-generated</li>
            <li>Enter your age, sex, and preferred language</li>
            <li>Enter or speak your main concern</li>
            <li>Fill symptoms, medications, existing conditions, and notes</li>
            <li>Check the consent box</li>
            <li>Click &ldquo;Get My Health Summary&rdquo;</li>
          </ol>
        )}
      </div>

      {/* Voice/Recording Status */}
      {isRecording && listeningField && (
        <div className="bg-red-50 border border-red-300 rounded-xl p-4 mb-4 flex items-center gap-3 animate-pulse" data-testid="recording-banner">
          <div className="w-3 h-3 bg-red-500 rounded-full animate-ping" />
          <div className="flex-1">
            <p className="text-sm text-red-800 font-medium">Recording in {formData.language}... Tap mic to stop</p>
            <p className="text-xs text-red-600">Speak clearly. Recording auto-stops at 25 seconds.</p>
          </div>
        </div>
      )}

      {/* Voice Error Banner */}
      {voiceError && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 mb-4 flex items-start gap-3" data-testid="voice-error-banner">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-amber-900 font-medium">{voiceError}</p>
            <p className="text-xs text-amber-700 mt-1">Typing always works — voice input is optional.</p>
            <button onClick={() => setVoiceError(null)} className="text-xs text-[#2563EB] hover:underline mt-1 font-medium">Dismiss</button>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="intake-card p-6 md:p-8" data-testid="intake-form">
        {/* Encounter Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wider">Encounter ID</Label>
            <div className="mt-1 px-4 py-2.5 bg-slate-100 rounded-xl text-sm text-slate-500 border border-slate-200" data-testid="encounter-id-display">
              {editMode && editEncounterId ? editEncounterId : 'Auto-generated on submission'}
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wider">Date/Time</Label>
            <div className="mt-1 px-4 py-2.5 bg-slate-100 rounded-xl text-sm text-slate-600 border border-slate-200" data-testid="datetime-display">
              {now.toLocaleDateString()} {now.toLocaleTimeString()}
            </div>
          </div>
        </div>

        {/* Demographics */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
          <div className="sm:col-span-1">
            <Label htmlFor="patientName" className="text-sm font-bold">Patient Name <span className="text-xs font-normal text-slate-400">(optional)</span></Label>
            <Input
              id="patientName"
              placeholder=""
              value={formData.patientName}
              onChange={e => updateField('patientName', e.target.value)}
              className="mt-1 bg-white border-2 border-slate-300 rounded-xl focus:border-[#2563EB]"
              data-testid="input-patient-name"
            />
          </div>
          <div>
            <Label htmlFor="age" className="text-sm font-bold">Age</Label>
            <Input id="age" placeholder="" value={formData.age} onChange={e => updateField('age', e.target.value)}
              className="mt-1 bg-white border-2 border-slate-300 rounded-xl focus:border-[#2563EB]" data-testid="input-age" />
          </div>
          <div>
            <Label className="text-sm font-bold">Sex</Label>
            <Select value={formData.sex} onValueChange={v => updateField('sex', v)}>
              <SelectTrigger className="mt-1 bg-white border-2 border-slate-300 rounded-xl focus:border-[#2563EB]" data-testid="select-sex">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Male">Male</SelectItem>
                <SelectItem value="Female">Female</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-bold flex items-center gap-1">
              <Globe className="w-3.5 h-3.5" /> Language
            </Label>
            <Select value={formData.language} onValueChange={v => updateField('language', v)}>
              <SelectTrigger className="mt-1 bg-white border-2 border-slate-300 rounded-xl focus:border-[#2563EB]" data-testid="select-language">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {Object.keys(SARVAM_LANG_MAP).map(lang => (
                  <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                ))}
                <SelectItem value="Mandarin" data-testid="lang-option-mandarin">Mandarin (中文) · text demo</SelectItem>
              </SelectContent>
            </Select>
            {TEXT_ONLY_LANGS.includes(formData.language) ? (
              <p className="text-[10px] text-amber-600 mt-1 font-medium" data-testid="mandarin-text-demo-note">Text demo · full voice support planned post-funding.</p>
            ) : (
              <p className="text-[10px] text-slate-400 mt-1">Voice input powered by Sarvam AI — 22+ Indian languages</p>
            )}
          </div>
        </div>

        {/* Pediatric Fields — shown when age <= 17 */}
        {(() => {
          const ageNum = parseInt(formData.age);
          const isPeds = !isNaN(ageNum) && ageNum <= 17;
          if (!isPeds && !formData.age.toLowerCase().includes('month') && !formData.age.toLowerCase().includes('infant')) return null;
          return (
            <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl" data-testid="pediatric-fields">
              <h4 className="text-sm font-bold text-[#2563EB] mb-3">Pediatric Assessment Fields</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-bold">Weight</Label>
                  <Input value={formData.pedWeight} onChange={e => updateField('pedWeight', e.target.value)}
                    className="mt-1 bg-white border-2 border-slate-300 rounded-xl focus:border-[#2563EB]" data-testid="input-ped-weight" />
                </div>
                <div>
                  <Label className="text-sm font-bold">Feeding / Oral Intake</Label>
                  <Select value={formData.pedFeeding} onValueChange={v => updateField('pedFeeding', v)}>
                    <SelectTrigger className="mt-1 bg-white border-2 border-slate-300 rounded-xl focus:border-[#2563EB]" data-testid="select-ped-feeding">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal intake</SelectItem>
                      <SelectItem value="reduced">Reduced intake</SelectItem>
                      <SelectItem value="poor">Poor intake / refusing</SelectItem>
                      <SelectItem value="none">No oral intake</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-bold">Urine Output</Label>
                  <Select value={formData.pedUrineOutput} onValueChange={v => updateField('pedUrineOutput', v)}>
                    <SelectTrigger className="mt-1 bg-white border-2 border-slate-300 rounded-xl focus:border-[#2563EB]" data-testid="select-ped-urine">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="decreased">Decreased</SelectItem>
                      <SelectItem value="minimal">Minimal / dry diapers</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-bold">Vaccination Status</Label>
                  <Select value={formData.pedVaccinationStatus} onValueChange={v => updateField('pedVaccinationStatus', v)}>
                    <SelectTrigger className="mt-1 bg-white border-2 border-slate-300 rounded-xl focus:border-[#2563EB]" data-testid="select-ped-vaccination">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="up-to-date">Up to date</SelectItem>
                      <SelectItem value="not-up-to-date">Not up to date</SelectItem>
                      <SelectItem value="unknown">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-bold">Caregiver Relationship</Label>
                  <Select value={formData.pedCaregiverRelationship} onValueChange={v => updateField('pedCaregiverRelationship', v)}>
                    <SelectTrigger className="mt-1 bg-white border-2 border-slate-300 rounded-xl focus:border-[#2563EB]" data-testid="select-ped-caregiver">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="parent">Parent</SelectItem>
                      <SelectItem value="guardian">Guardian</SelectItem>
                      <SelectItem value="other">Other caregiver</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ─── Devarajan Intake v2 — Structured Clinical Fields (May 21, 2026) ──
            Field order per spec:
              3. Chief Complaint (free text + Active table + Inactive table)
              4. Symptoms — 9 OLDCARTS sub-fields
              5. Medications (updated label)
              6. Allergies (NEW, required)
              7. Active Conditions  8. Past Medical History  9. Family History (NEW)
             10. Former Exposures  11. Additional Notes
            Past Surgical History, Social Wellbeing, Care Team — below this block (unchanged). */}
        {(() => {
          const L = v2Labels(formData.language);
          const activeRows = parseV2Lines(formData.chiefComplaintActiveText);
          const inactiveRows = parseV2Lines(formData.chiefComplaintInactiveText);
          const renderRowsPreview = (rows, testidPrefix) => rows.length === 0 ? null : (
            <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 overflow-hidden" data-testid={`${testidPrefix}-preview`}>
              <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100">{L.cc_preview}</div>
              <table className="w-full text-xs">
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-t border-slate-200">
                      <td className="px-3 py-1.5 text-slate-800">{r.condition}</td>
                      <td className="px-3 py-1.5 text-slate-500 italic whitespace-nowrap">{r.date || L.cc_no_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
          return (
            <>
              {/* 3 — CHIEF COMPLAINT */}
              <div className="mb-6 p-4 border-2 border-slate-300 rounded-xl bg-slate-50" data-testid="cc-v2-block">
                <Label className="text-sm font-bold">{L.cc_section} <span className="text-red-500">*</span></Label>
                <p className="text-[11px] text-slate-500 italic mt-0.5">{L.cc_subtitle}</p>

                {/* 1A Free-text */}
                <div className="relative mt-3">
                  <Textarea
                    id="chiefComplaint"
                    value={formData.chiefComplaint}
                    onChange={e => updateField('chiefComplaint', e.target.value)}
                    onBlur={() => handleFieldBlur('chiefComplaint')}
                    placeholder=""
                    className="bg-white border-2 border-slate-300 rounded-xl pr-10 min-h-[80px] text-[15px] focus:border-[#2563EB]"
                    data-testid="input-chiefComplaint"
                  />
                  <VoiceBtn field="chiefComplaint" />
                </div>
                {(spellCorrections.chiefComplaint || []).map((c, ci) => (
                  <p key={ci} className="text-xs italic text-slate-500 mt-1" data-testid={`spell-correction-chiefComplaint-${ci}`}>
                    ✓ Spelling auto-corrected: {c.from} → {c.to}
                  </p>
                ))}

                {/* 1B Active Process */}
                <div className="mt-4">
                  <Label className="text-sm font-bold">{L.cc_active}</Label>
                  <p className="text-[11px] text-slate-500 italic mt-0.5">{L.cc_active_sub}</p>
                  <div className="relative mt-1">
                    <Textarea
                      id="chiefComplaintActiveText"
                      value={formData.chiefComplaintActiveText}
                      onChange={e => updateField('chiefComplaintActiveText', e.target.value)}
                      placeholder={'e.g.\nChest pain 5-8-26\nPenicillin allergy lifelong\nHypertension 2015'}
                      className="bg-white border-2 border-slate-300 rounded-xl pr-10 min-h-[80px] text-[14px] focus:border-[#2563EB] font-mono"
                      data-testid="input-chiefComplaintActiveText"
                    />
                    <VoiceBtn field="chiefComplaintActiveText" />
                  </div>
                  {renderRowsPreview(activeRows, 'cc-active')}
                </div>

                {/* 1C Inactive Process */}
                <div className="mt-4">
                  <Label className="text-sm font-bold">{L.cc_inactive}</Label>
                  <p className="text-[11px] text-slate-500 italic mt-0.5">{L.cc_inactive_sub}</p>
                  <div className="relative mt-1">
                    <Textarea
                      id="chiefComplaintInactiveText"
                      value={formData.chiefComplaintInactiveText}
                      onChange={e => updateField('chiefComplaintInactiveText', e.target.value)}
                      placeholder={'e.g.\nAppendectomy 10-1-70\nFemur fracture 1998\nPneumonia April 2018'}
                      className="bg-white border-2 border-slate-300 rounded-xl pr-10 min-h-[70px] text-[14px] focus:border-[#2563EB] font-mono"
                      data-testid="input-chiefComplaintInactiveText"
                    />
                    <VoiceBtn field="chiefComplaintInactiveText" />
                  </div>
                  {renderRowsPreview(inactiveRows, 'cc-inactive')}
                </div>
              </div>

              {/* 4 — SYMPTOMS (OLDCARTS) */}
              <div className="mb-6 p-4 border-2 border-slate-300 rounded-xl bg-white" data-testid="symptoms-v2-block">
                <Label className="text-sm font-bold">{L.sx_section}</Label>
                <p className="text-[11px] text-slate-500 italic mt-0.5">{L.sx_subtitle}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  {[
                    { key: 'sxOnset', label: L.sx_onset },
                    { key: 'sxLocation', label: L.sx_location },
                    { key: 'sxDuration', label: L.sx_duration },
                    { key: 'sxCharacter', label: L.sx_character },
                    { key: 'sxRadiation', label: L.sx_radiation },
                    { key: 'sxAggravating', label: L.sx_aggravating },
                    { key: 'sxRelieving', label: L.sx_relieving },
                    { key: 'sxTiming', label: L.sx_timing },
                    { key: 'sxSeverity', label: L.sx_severity },
                    { key: 'sxAssociatedSymptoms', label: L.sx_associated, span2: true },
                  ].map(({ key, label, span2 }) => (
                    <div key={key} className={span2 ? 'md:col-span-2' : ''} data-testid={`sx-field-${key}`}>
                      <Label htmlFor={key} className="text-xs font-bold text-slate-700">{label}</Label>
                      <div className="relative mt-1">
                        <Textarea
                          id={key}
                          value={formData[key]}
                          onChange={e => updateField(key, e.target.value)}
                          placeholder=""
                          className="bg-white border-2 border-slate-300 rounded-xl pr-10 min-h-[60px] text-[14px] focus:border-[#2563EB]"
                          data-testid={`input-${key}`}
                        />
                        <VoiceBtn field={key} />
                      </div>
                    </div>
                  ))}
                </div>
                {/* Legacy free-text Symptoms — preserved so backward-compat encounters keep working */}
                <div className="mt-3">
                  <Label htmlFor="symptoms" className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Additional symptoms (optional free-text)</Label>
                  <div className="relative mt-1">
                    <Textarea
                      id="symptoms"
                      value={formData.symptoms}
                      onChange={e => updateField('symptoms', e.target.value)}
                      onBlur={() => handleFieldBlur('symptoms')}
                      placeholder=""
                      className="bg-white border border-slate-200 rounded-xl pr-10 min-h-[50px] text-[13px] focus:border-[#2563EB]"
                      data-testid="input-symptoms"
                    />
                    <VoiceBtn field="symptoms" />
                  </div>
                </div>
              </div>

              {/* Phase B.5 — Vital Signs (optional, structured) */}
              <div className="mb-5 p-3 border-2 border-sky-200 rounded-xl bg-sky-50/40" data-testid="vitals-b5-block">
                <Label className="text-sm font-bold">Vital Signs (optional)</Label>
                <p className="text-[11px] text-slate-500 italic mt-0.5">If measured, add your vitals — these sharpen early-warning scoring (e.g., NEWS2, CURB-65, BP staging).</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                  {[
                    { key: 'vitalHr', label: 'Heart rate (bpm)', ph: 'e.g., 96' },
                    { key: 'vitalRr', label: 'Resp. rate (/min)', ph: 'e.g., 18' },
                    { key: 'vitalSpo2', label: 'SpO₂ (%)', ph: 'e.g., 97' },
                    { key: 'vitalSbp', label: 'Systolic BP', ph: 'e.g., 120' },
                    { key: 'vitalDbp', label: 'Diastolic BP', ph: 'e.g., 80' },
                    { key: 'vitalTemp', label: 'Temp (°C)', ph: 'e.g., 37.0' },
                  ].map(v => (
                    <div key={v.key}>
                      <Label htmlFor={v.key} className="text-[11px] font-semibold text-slate-600">{v.label}</Label>
                      <Input id={v.key} type="number" inputMode="decimal" value={formData[v.key]} onChange={e => updateField(v.key, e.target.value)}
                        placeholder={v.ph} className="bg-white border-2 border-slate-300 rounded-lg h-9 text-[14px] mt-0.5 focus:border-sky-500" data-testid={`input-${v.key}`} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Phase B.5 — Conditional follow-up (deterministic CC keyword reveal, NO LLM) */}
              {(() => {
                const cluster = matchFollowUpCluster(formData.chiefComplaint);
                if (!cluster) return null;
                return (
                  <div className="mb-5 p-3 border-2 border-violet-200 rounded-xl bg-violet-50/40" data-testid="followup-b5-block">
                    <Label className="text-sm font-bold" data-testid="followup-b5-title">{cluster.title}</Label>
                    <p className="text-[11px] text-slate-500 italic mt-0.5">Optional — these answers, tailored to your chief complaint, help your clinician.</p>
                    <div className="space-y-2 mt-2">
                      {cluster.questions.map(q => (
                        <div key={q.key}>
                          <Label htmlFor={`followup-${q.key}`} className="text-[12px] font-semibold text-slate-600">{q.label}</Label>
                          <Input id={`followup-${q.key}`} value={formData.followUp?.[q.key] || ''}
                            onChange={e => setFormData(prev => ({ ...prev, followUp: { ...prev.followUp, [q.key]: e.target.value } }))}
                            placeholder={q.placeholder} className="bg-white border-2 border-slate-300 rounded-lg h-9 text-[14px] mt-0.5 focus:border-violet-500" data-testid={`input-followup-${q.key}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* 5 — MEDICATIONS (updated label) */}
              <div className="mb-5" data-testid="meds-v2-block">
                <Label htmlFor="medications" className="text-sm font-bold">{L.meds_label}</Label>
                <p className="text-[11px] text-slate-500 italic mt-0.5">{L.meds_sub}</p>
                <div className="relative mt-1">
                  <Textarea
                    id="medications"
                    value={formData.medications}
                    onChange={e => updateField('medications', e.target.value)}
                    onBlur={() => handleFieldBlur('medications')}
                    placeholder=""
                    className="bg-white border-2 border-slate-300 rounded-xl pr-10 min-h-[80px] text-[15px] focus:border-[#2563EB]"
                    data-testid="input-medications"
                  />
                  <VoiceBtn field="medications" />
                </div>
              </div>

              {/* Phase B.5 — Recent Labs (optional, free text) */}
              <div className="mb-5" data-testid="recent-labs-b5-block">
                <Label htmlFor="recentLabs" className="text-sm font-bold">Recent Labs (optional)</Label>
                <p className="text-[11px] text-slate-500 italic mt-0.5">Recent results, if known — helps compute scores (e.g., MELD, FIB-4, Child-Pugh, CURB-65). Include value and units.</p>
                <Textarea id="recentLabs" value={formData.recentLabs} onChange={e => updateField('recentLabs', e.target.value)}
                  placeholder="e.g., bilirubin 2.1 mg/dL, INR 1.6, albumin 3.0, urea 9 mmol/L, platelets 95" className="bg-white border-2 border-slate-300 rounded-xl min-h-[70px] text-[15px] mt-1 focus:border-[#2563EB]" data-testid="input-recentLabs" />
              </div>

              {/* PHQ-9 / GAD-7 — conditional mood & anxiety screen (July 2026) */}
              {(() => {
                const moodText = `${formData.chiefComplaint} ${formData.symptoms} ${formData.notes} ${formData.existingConditions} ${formData.historicalConditions}`;
                const autoDetected = MOOD_KEYWORDS.test(moodText);
                const show = autoDetected || moodScreenManual;
                const phq = Array.isArray(formData.phq9Responses) ? formData.phq9Responses : [];
                const gad = Array.isArray(formData.gad7Responses) ? formData.gad7Responses : [];
                const phqAnswered = phq.filter(v => v !== null && v !== undefined);
                const gadAnswered = gad.filter(v => v !== null && v !== undefined);
                const phqTotal = phqAnswered.reduce((a, b) => a + Number(b), 0);
                const gadTotal = gadAnswered.reduce((a, b) => a + Number(b), 0);
                const phqComplete = phqAnswered.length === 9;
                const gadComplete = gadAnswered.length === 7;
                const item9 = phq[8];

                const renderInstrument = (instrument, items, values) => (
                  <div className="mt-3" data-testid={`${instrument}-block`}>
                    <p className="text-[12px] font-bold text-slate-700 mb-2">
                      {instrument === 'phq9'
                        ? 'PHQ-9 — Over the last 2 weeks, how often have you been bothered by:'
                        : 'GAD-7 — Over the last 2 weeks, how often have you been bothered by:'}
                    </p>
                    <div className="space-y-2">
                      {items.map((label, i) => (
                        <div key={i} className="rounded-lg bg-white border border-indigo-100 p-2" data-testid={`${instrument}-item-${i}`}>
                          <p className="text-[13px] text-slate-700 mb-1.5 leading-snug">{i + 1}. {label}</p>
                          <div className="flex flex-wrap gap-1.5">
                            {MOOD_SCALE.map(opt => {
                              const selected = values[i] !== null && values[i] !== undefined && Number(values[i]) === opt.v;
                              return (
                                <button
                                  key={opt.v}
                                  type="button"
                                  onClick={() => setMoodItem(instrument, i, opt.v)}
                                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${selected ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-indigo-300'}`}
                                  data-testid={`${instrument}-item-${i}-opt-${opt.v}`}
                                >
                                  {opt.v} · {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );

                return (
                  <div className="mb-5" data-testid="mood-screen-section">
                    {!show && (
                      <button
                        type="button"
                        onClick={() => setMoodScreenManual(true)}
                        className="w-full text-left p-3 rounded-xl border-2 border-dashed border-indigo-200 bg-indigo-50/40 text-[13px] text-indigo-700 font-semibold hover:bg-indigo-50 transition-colors"
                        data-testid="mood-screen-toggle"
                      >
                        + Add mood &amp; anxiety screening (PHQ-9 / GAD-7)
                      </button>
                    )}
                    {show && (
                      <div className="p-3 border-2 border-indigo-300 rounded-xl bg-indigo-50/40" data-testid="mood-anxiety-screen">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <Label className="text-sm font-bold text-indigo-900">Mood &amp; Anxiety Screening</Label>
                            <p className="text-[11px] text-slate-500 italic mt-0.5">
                              {autoDetected
                                ? 'Mood/anxiety mentioned — optional validated screens (PHQ-9 & GAD-7). Scored automatically on submit; observational only.'
                                : 'Optional validated screens (PHQ-9 & GAD-7). Scored automatically on submit; observational only.'}
                            </p>
                          </div>
                          {!autoDetected && (
                            <button type="button" onClick={() => setMoodScreenManual(false)}
                              className="text-[11px] text-slate-400 hover:text-slate-600 underline shrink-0" data-testid="mood-screen-hide">
                              Hide
                            </button>
                          )}
                        </div>

                        {renderInstrument('phq9', PHQ9_ITEMS, phq)}
                        <div className="mt-2 flex items-center gap-3 text-[12px]" data-testid="phq9-score">
                          <span className="font-bold text-indigo-800">PHQ-9: {phqTotal}/27</span>
                          {phqComplete && <Badge className="bg-indigo-100 text-indigo-800 border-0" data-testid="phq9-band">{_bandPhq9(phqTotal)}</Badge>}
                          {!phqComplete && <span className="text-slate-400 italic">{phqAnswered.length}/9 answered</span>}
                        </div>
                        {phqComplete && Number(item9) > 0 && (
                          <p className="mt-1 text-[12px] font-semibold text-rose-700 flex items-center gap-1" data-testid="phq9-item9-flag">
                            <AlertCircle className="w-3.5 h-3.5" /> Item 9 &gt; 0 — clinician suicide-risk assessment warranted regardless of total.
                          </p>
                        )}

                        {renderInstrument('gad7', GAD7_ITEMS, gad)}
                        <div className="mt-2 flex items-center gap-3 text-[12px]" data-testid="gad7-score">
                          <span className="font-bold text-indigo-800">GAD-7: {gadTotal}/21</span>
                          {gadComplete && <Badge className="bg-indigo-100 text-indigo-800 border-0" data-testid="gad7-band">{_bandGad7(gadTotal)}</Badge>}
                          {!gadComplete && <span className="text-slate-400 italic">{gadAnswered.length}/7 answered</span>}
                        </div>

                        <p className="mt-3 text-[10px] text-slate-400 leading-snug">
                          Deterministic scoring (Kroenke 2001 / Spitzer 2006). No LLM. Fully-answered screens are saved to the patient's longitudinal trend on submit; partial screens are not sent.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}


              {/* 6 — ALLERGIES (NEW, REQUIRED) */}
              <div className="mb-5 p-3 border-2 border-rose-300 rounded-xl bg-rose-50/40" data-testid="allergies-v2-block">
                <Label htmlFor="allergies" className="text-sm font-bold">{L.allergies_label} <span className="text-red-500">*</span></Label>
                <p className="text-[11px] text-slate-500 italic mt-0.5">{L.allergies_sub}</p>
                <div className="relative mt-1">
                  <Textarea
                    id="allergies"
                    value={formData.allergies}
                    onChange={e => updateField('allergies', e.target.value)}
                    placeholder='e.g., Penicillin (anaphylaxis), Peanut, Latex — or "None known"'
                    className="bg-white border-2 border-rose-300 rounded-xl pr-10 min-h-[70px] text-[15px] focus:border-rose-500"
                    data-testid="input-allergies"
                  />
                  <VoiceBtn field="allergies" />
                </div>
              </div>

              {/* 7 — Active Conditions */}
              <div className="mb-5">
                <Label htmlFor="existingConditions" className="text-sm font-bold">Active Conditions (currently managed)</Label>
                <div className="relative mt-1">
                  <Textarea id="existingConditions" placeholder="e.g., Type 2 diabetes, hypertension, active CKD" value={formData.existingConditions} onChange={e => updateField('existingConditions', e.target.value)} onBlur={() => handleFieldBlur('existingConditions')} className="bg-white border-2 border-slate-300 rounded-xl pr-10 min-h-[80px] text-[15px] focus:border-[#2563EB]" data-testid="input-existingConditions" />
                  <VoiceBtn field="existingConditions" />
                </div>
                {(spellCorrections.existingConditions || []).map((c, ci) => (
                  <p key={ci} className="text-xs italic text-slate-500 mt-1" data-testid={`spell-correction-existingConditions-${ci}`}>✓ Spelling auto-corrected: {c.from} → {c.to}</p>
                ))}
                {/* Phase B.5 — Immunocompromise checkbox */}
                <div className="flex items-start gap-2 mt-3" data-testid="immunocompromised-b5-row">
                  <Checkbox id="immunocompromised" checked={formData.immunocompromised} onCheckedChange={v => updateField('immunocompromised', !!v)} className="mt-0.5" data-testid="checkbox-immunocompromised" />
                  <Label htmlFor="immunocompromised" className="text-[13px] text-slate-700 cursor-pointer leading-snug">Immunocompromised (e.g., chemotherapy, transplant, HIV, asplenia, high-dose steroids)</Label>
                </div>
              </div>

              {/* 8 — Past Medical History */}
              <div className="mb-5">
                <Label htmlFor="historicalConditions" className="text-sm font-bold">Past Medical History — resolved / inactive</Label>
                <p className="text-[11px] text-slate-500 italic mt-0.5">Not seen by clinical risk engines.</p>
                <div className="relative mt-1">
                  <Textarea id="historicalConditions" placeholder="e.g., femur fracture from youth, appendectomy 2010, h/o pneumonia 2018 — these will appear in your record but will NOT trigger active risk alerts" value={formData.historicalConditions} onChange={e => updateField('historicalConditions', e.target.value)} onBlur={() => handleFieldBlur('historicalConditions')} className="bg-white border-2 border-slate-300 rounded-xl pr-10 min-h-[80px] text-[15px] focus:border-[#2563EB]" data-testid="input-historicalConditions" />
                  <VoiceBtn field="historicalConditions" />
                </div>
              </div>

              {/* 9 — FAMILY HISTORY (NEW) */}
              <div className="mb-5" data-testid="fh-v2-block">
                <Label htmlFor="familyHistory" className="text-sm font-bold">{L.fh_label}</Label>
                <p className="text-[11px] text-slate-500 italic mt-0.5">{L.fh_sub}</p>
                <div className="relative mt-1">
                  <Textarea
                    id="familyHistory"
                    value={formData.familyHistory}
                    onChange={e => updateField('familyHistory', e.target.value)}
                    placeholder="e.g., Father MI age 52; Mother T2DM; Sister breast cancer age 45"
                    className="bg-white border-2 border-slate-300 rounded-xl pr-10 min-h-[70px] text-[15px] focus:border-[#2563EB]"
                    data-testid="input-familyHistory"
                  />
                  <VoiceBtn field="familyHistory" />
                </div>
              </div>

              {/* 10 — Former Exposures */}
              <div className="mb-5">
                <Label htmlFor="formerExposures" className="text-sm font-bold">Former Exposures</Label>
                <p className="text-[11px] text-slate-500 italic mt-0.5">Not seen by clinical risk engines.</p>
                <div className="relative mt-1">
                  <Textarea id="formerExposures" placeholder="e.g., former smoker (quit 30 years ago), ex-drinker, discontinued opioid use" value={formData.formerExposures} onChange={e => updateField('formerExposures', e.target.value)} onBlur={() => handleFieldBlur('formerExposures')} className="bg-white border-2 border-slate-300 rounded-xl pr-10 min-h-[80px] text-[15px] focus:border-[#2563EB]" data-testid="input-formerExposures" />
                  <VoiceBtn field="formerExposures" />
                </div>
              </div>

              {/* 11 — Additional Notes */}
              <div className="mb-5">
                <Label htmlFor="notes" className="text-sm font-bold">Additional Notes</Label>
                <div className="relative mt-1">
                  <Textarea id="notes" placeholder="" value={formData.notes} onChange={e => updateField('notes', e.target.value)} onBlur={() => handleFieldBlur('notes')} className="bg-white border-2 border-slate-300 rounded-xl pr-10 min-h-[80px] text-[15px] focus:border-[#2563EB]" data-testid="input-notes" />
                  <VoiceBtn field="notes" />
                </div>
              </div>
            </>
          );
        })()}


        {/* ─── Surgical History — structured capture (May 17, 2026) ───────────
            Why this exists: anesthesiologists, surgeons, and Engine 35 (pre-op
            risk stratification) need a structured prior-surgery list. Today's
            free-text "Notes" buries it. This section keeps deterministic
            structure while preserving voice-or-text input. NO LLM. */}
        <div className="mb-6 p-4 border-2 border-slate-300 rounded-xl bg-slate-50" data-testid="surgical-history-block">
          <div className="flex items-center justify-between mb-2">
            <div>
              <Label htmlFor="surgicalHistoryFreeText" className="text-sm font-bold">
                Past Surgical History <span className="text-[11px] font-normal text-slate-500">(optional — strongly recommended)</span>
              </Label>
              <p className="text-[11px] text-slate-500 italic mt-0.5">
                Critical for pre-operative clearance, anesthesia safety, and surgeon prep. Capture anything from minor procedures (tonsillectomy, hernia repair) to major surgeries (CABG, transplant, joint replacement). Year ranges are fine if exact dates unknown.
              </p>
            </div>
          </div>

          {/* Free-text field (use this if you want to speak or list quickly) */}
          <div className="relative mt-2">
            <Textarea
              id="surgicalHistoryFreeText"
              placeholder="e.g., appendectomy 2003, gallbladder removed 2015, right knee replacement 2021 with mild post-op DVT"
              value={formData.surgicalHistoryFreeText}
              onChange={e => updateField('surgicalHistoryFreeText', e.target.value)}
              className="bg-white border-2 border-slate-300 rounded-xl pr-10 min-h-[60px] text-[15px] focus:border-[#2563EB]"
              data-testid="input-surgicalHistoryFreeText"
            />
            <VoiceBtn field="surgicalHistoryFreeText" />
          </div>

          {/* Structured rows — optional, additive */}
          <div className="mt-3">
            <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-2">Or list each surgery individually (preferred — helps the surgeon &amp; anesthesiologist):</div>
            {(formData.surgicalHistoryRows || []).map((row, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 mb-2" data-testid={`sx-row-${idx}`}>
                <input
                  type="text"
                  placeholder="Procedure (e.g., CABG, cholecystectomy)"
                  value={row.procedure || ''}
                  onChange={e => {
                    const rows = [...(formData.surgicalHistoryRows || [])];
                    rows[idx] = { ...rows[idx], procedure: e.target.value };
                    updateField('surgicalHistoryRows', rows);
                  }}
                  className="col-span-5 px-3 py-2 text-sm border-2 border-slate-300 rounded-lg bg-white"
                  data-testid={`sx-procedure-${idx}`}
                />
                <input
                  type="text"
                  placeholder="Year"
                  value={row.year || ''}
                  onChange={e => {
                    const rows = [...(formData.surgicalHistoryRows || [])];
                    rows[idx] = { ...rows[idx], year: e.target.value };
                    updateField('surgicalHistoryRows', rows);
                  }}
                  className="col-span-2 px-3 py-2 text-sm border-2 border-slate-300 rounded-lg bg-white"
                  data-testid={`sx-year-${idx}`}
                />
                <input
                  type="text"
                  placeholder="Facility (optional)"
                  value={row.facility || ''}
                  onChange={e => {
                    const rows = [...(formData.surgicalHistoryRows || [])];
                    rows[idx] = { ...rows[idx], facility: e.target.value };
                    updateField('surgicalHistoryRows', rows);
                  }}
                  className="col-span-3 px-3 py-2 text-sm border-2 border-slate-300 rounded-lg bg-white"
                  data-testid={`sx-facility-${idx}`}
                />
                <input
                  type="text"
                  placeholder="Complication"
                  value={row.complication || ''}
                  onChange={e => {
                    const rows = [...(formData.surgicalHistoryRows || [])];
                    rows[idx] = { ...rows[idx], complication: e.target.value };
                    updateField('surgicalHistoryRows', rows);
                  }}
                  className="col-span-2 px-3 py-2 text-sm border-2 border-slate-300 rounded-lg bg-white"
                  data-testid={`sx-complication-${idx}`}
                />
              </div>
            ))}
            <div className="flex gap-2 mt-1">
              <button
                type="button"
                onClick={() => {
                  const rows = [...(formData.surgicalHistoryRows || []), { procedure: '', year: '', facility: '', complication: '' }];
                  updateField('surgicalHistoryRows', rows);
                }}
                className="text-xs text-[#2563EB] hover:text-[#1e4fa8] underline font-semibold"
                data-testid="sx-add-row"
              >
                + Add another surgery
              </button>
              {(formData.surgicalHistoryRows || []).length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    const rows = (formData.surgicalHistoryRows || []).slice(0, -1);
                    updateField('surgicalHistoryRows', rows);
                  }}
                  className="text-xs text-slate-500 hover:text-slate-700 underline ml-3"
                  data-testid="sx-remove-row"
                >
                  − Remove last row
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Founder-only Contact & Feedback Capture — for post-demo lead capture */}
        {isFounder && (
          <div className="mb-6 p-4 bg-emerald-50 border-2 border-emerald-300 rounded-xl" data-testid="contact-capture-block">
            <h4 className="text-sm font-bold text-emerald-800 mb-1">Physician / Visitor Contact (Optional)</h4>
            <p className="text-xs text-emerald-700 mb-3">Capture who you demoed to. Fine to fill in after the demo — just edit this intake later.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Name</Label>
                <Input value={formData.contactName} onChange={e => updateField('contactName', e.target.value)}
                  placeholder="Dr. ..." className="mt-1 bg-white border-2 border-slate-300 rounded-xl focus:border-emerald-500" data-testid="input-contact-name" />
              </div>
              <div>
                <Label className="text-sm font-medium">Email</Label>
                <Input type="email" value={formData.contactEmail} onChange={e => updateField('contactEmail', e.target.value)}
                  placeholder="name@clinic.com" className="mt-1 bg-white border-2 border-slate-300 rounded-xl focus:border-emerald-500" data-testid="input-contact-email" />
              </div>
              <div>
                <Label className="text-sm font-medium">Role</Label>
                <Select value={formData.contactRole} onValueChange={v => updateField('contactRole', v)}>
                  <SelectTrigger className="mt-1 bg-white border-2 border-slate-300 rounded-xl focus:border-emerald-500" data-testid="select-contact-role">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="physician">Physician</SelectItem>
                    <SelectItem value="nurse">Nurse</SelectItem>
                    <SelectItem value="clinic_admin">Clinic Admin</SelectItem>
                    <SelectItem value="patient">Patient</SelectItem>
                    <SelectItem value="investor">Investor</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-sm font-medium">Feedback / Notes</Label>
              <Textarea value={formData.contactFeedback} onChange={e => updateField('contactFeedback', e.target.value)}
                placeholder="e.g. 'Impressed by SOAP output, wants beta access for 3 physicians.'"
                className="mt-1 bg-white border-2 border-slate-300 rounded-xl min-h-[70px] focus:border-emerald-500" data-testid="input-contact-feedback" />
            </div>
          </div>
        )}

        {/* Social Wellbeing — Engine 25 (universal anchor + conditional UCLA-3, deterministic) */}
        <div className="mb-6 p-4 bg-indigo-50 border-2 border-indigo-200 rounded-xl" data-testid="social-wellbeing-block">
          <h4 className="text-sm font-bold text-indigo-900 mb-1">Social Wellbeing</h4>
          <p className="text-xs text-indigo-700 mb-3">
            How you are connected to others matters for your health. Your answer is confidential and helps your care team support you better.
          </p>

          <Label className="text-sm font-medium text-slate-800">
            In the past month, how often have you felt isolated or lacking companionship?
          </Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
            {['Never', 'Rarely', 'Sometimes', 'Often'].map(opt => (
              <button
                key={opt}
                type="button"
                onClick={() => updateField('lonelinessAnchor', opt)}
                className={
                  'px-3 py-2 rounded-xl text-sm border-2 transition-all ' +
                  (formData.lonelinessAnchor === opt
                    ? 'bg-indigo-600 text-white border-indigo-600 font-bold'
                    : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-400')
                }
                data-testid={`loneliness-anchor-${opt.toLowerCase()}`}
              >
                {opt}
              </button>
            ))}
          </div>

          {/* Conditional UCLA-3 — appears only when anchor is Sometimes / Often */}
          {['Sometimes', 'Often'].includes(formData.lonelinessAnchor) && (
            <div className="mt-5 pt-4 border-t-2 border-indigo-200" data-testid="ucla3-block">
              <p className="text-xs italic text-indigo-700 mb-3">
                Thank you for sharing. A brief 3-question screen (UCLA-3, a validated tool) will help your care team understand better.
              </p>
              {[
                { field: 'ucla3LackCompanionship', q: 'How often do you feel that you lack companionship?' },
                { field: 'ucla3FeelLeftOut', q: 'How often do you feel left out?' },
                { field: 'ucla3FeelIsolated', q: 'How often do you feel isolated from others?' },
              ].map(({ field, q }) => (
                <div key={field} className="mb-3">
                  <Label className="text-sm text-slate-800">{q}</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1.5">
                    {['Hardly ever', 'Some of the time', 'Often'].map(opt => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => updateField(field, opt)}
                        className={
                          'px-2 py-1.5 rounded-lg text-xs border-2 transition-all ' +
                          (formData[field] === opt
                            ? 'bg-indigo-600 text-white border-indigo-600 font-bold'
                            : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-400')
                        }
                        data-testid={`${field}-${opt.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              <div className="flex items-center gap-2 mt-3 p-2 bg-white rounded-lg">
                <Checkbox
                  id="bereavementRecent"
                  checked={formData.bereavementRecent}
                  onCheckedChange={v => updateField('bereavementRecent', !!v)}
                  data-testid="checkbox-bereavement"
                />
                <Label htmlFor="bereavementRecent" className="text-xs text-slate-700 cursor-pointer">
                  Have you lost a close family member or partner in the past 12 months?
                </Label>
              </div>
            </div>
          )}
        </div>

        {/* Care Team — Optional (patient-reported, unverified, text-only Phase 1) */}
        <div className="mb-6 p-4 bg-slate-50 border-2 border-slate-200 rounded-xl" data-testid="care-team-block">
          <h4 className="text-sm font-bold text-slate-800 mb-1">Care Team — Optional</h4>
          <p className="text-xs text-slate-500 mb-3">Help us connect your health summary to your care team. All fields optional.</p>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label className="text-sm font-medium">Primary Care Physician (Optional)</Label>
              <Input
                value={formData.primaryPhysician}
                onChange={e => updateField('primaryPhysician', e.target.value)}
                placeholder="Dr. Name — clinic — phone or email"
                className="mt-1 bg-white border-2 border-slate-300 rounded-xl focus:border-[#2563EB]"
                data-testid="input-primary-physician"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Specialist (Optional)</Label>
              <Input
                value={formData.specialist}
                onChange={e => updateField('specialist', e.target.value)}
                placeholder="Dr. Name — specialty — clinic"
                className="mt-1 bg-white border-2 border-slate-300 rounded-xl focus:border-[#2563EB]"
                data-testid="input-specialist"
              />
            </div>
          </div>
        </div>

        {/* Communication & Caregiver */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3">
          <div>
            <Label className="text-sm font-bold">Preferred Communication</Label>
            <Select value={formData.communicationMode} onValueChange={v => updateField('communicationMode', v)}>
              <SelectTrigger className="mt-1 bg-white border-2 border-slate-300 rounded-xl focus:border-[#2563EB]" data-testid="select-communication">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="voice">Voice</SelectItem>
                <SelectItem value="both">Both</SelectItem>
                <SelectItem value="sign_language">Sign Language</SelectItem>
                <SelectItem value="aac">AAC / Pictogram</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-bold">Region <span className="text-[10px] font-normal text-slate-500">(for resource bundles)</span></Label>
            <Select value={formData.region || 'auto'} onValueChange={v => updateField('region', v === 'auto' ? '' : v)}>
              <SelectTrigger className="mt-1 bg-white border-2 border-slate-300 rounded-xl focus:border-[#2563EB]" data-testid="select-region">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto-detect</SelectItem>
                <SelectItem value="US">United States</SelectItem>
                <SelectItem value="India">India</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch
              checked={formData.caregiverAssisted}
              onCheckedChange={v => updateField('caregiverAssisted', v)}
              data-testid="switch-caregiver"
            />
            <Label className="text-sm">Caregiver/assistant helping</Label>
          </div>
        </div>

        {/* Engine 49 — Accessibility detail block (renders only when sign-language / AAC / interpreter signaled) */}
        {(formData.communicationMode === 'sign_language' || formData.communicationMode === 'aac' || formData.interpreterNeeded) && (
          <div className="rounded-xl border-2 border-violet-300 bg-violet-50 p-4 mb-6" data-testid="accessibility-detail-block">
            <p className="text-sm font-bold text-violet-900 mb-2">Accessibility — communication detail</p>
            <p className="text-xs text-slate-700 mb-3">We will flag this encounter for your clinic so an interpreter or accessibility resource is arranged. Per ADA / HHS §1557, qualified interpreters are provided at no cost to the patient.</p>

            {formData.communicationMode === 'sign_language' && (
              <div className="mb-3">
                <Label className="text-xs font-bold">Which sign language do you use?</Label>
                <Select value={formData.signLanguage} onValueChange={v => updateField('signLanguage', v)}>
                  <SelectTrigger className="mt-1 bg-white border border-violet-300 rounded-md text-sm" data-testid="select-sign-language">
                    <SelectValue placeholder="Choose..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ASL">American Sign Language (ASL)</SelectItem>
                    <SelectItem value="ISL">Indian Sign Language (ISL)</SelectItem>
                    <SelectItem value="BSL">British Sign Language (BSL)</SelectItem>
                    <SelectItem value="LSF">Langue des Signes Française (LSF)</SelectItem>
                    <SelectItem value="Auslan">Australian Sign Language (Auslan)</SelectItem>
                    <SelectItem value="Other">Other (specify in notes)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center gap-3 mb-3">
              <Switch
                checked={formData.interpreterNeeded}
                onCheckedChange={v => updateField('interpreterNeeded', v)}
                data-testid="switch-interpreter-needed"
              />
              <Label className="text-xs">I need an interpreter for this visit</Label>
            </div>

            {formData.interpreterNeeded && (
              <div className="mb-2">
                <Label className="text-xs font-bold">Interpreter type</Label>
                <Select value={formData.interpreterType} onValueChange={v => updateField('interpreterType', v)}>
                  <SelectTrigger className="mt-1 bg-white border border-violet-300 rounded-md text-sm" data-testid="select-interpreter-type">
                    <SelectValue placeholder="Choose..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_person">In-person interpreter</SelectItem>
                    <SelectItem value="VRI">Video Remote Interpreting (VRI)</SelectItem>
                    <SelectItem value="phone">Phone interpreter (spoken languages only)</SelectItem>
                    <SelectItem value="family_member">Family member (not preferred for clinical)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        {/* Consent — DPDPA 2023-compliant, multilingual */}
        <div className="bg-slate-50 rounded-xl p-4 mb-6 border border-slate-200" data-testid="consent-block">
          <div className="flex items-start gap-3">
            <Checkbox
              checked={formData.consentGiven}
              onCheckedChange={v => updateField('consentGiven', v)}
              data-testid="consent-checkbox"
              className="mt-0.5"
            />
            <div className="text-sm text-slate-700">
              <p className="font-medium mb-1" data-testid="consent-title">
                {(CONSENT_TEXT[formData.language] || CONSENT_TEXT.English).title}
              </p>
              <p className="text-xs text-slate-500 leading-relaxed" data-testid="consent-body">
                {(CONSENT_TEXT[formData.language] || CONSENT_TEXT.English).body}
              </p>
              {!CONSENT_TEXT[formData.language] && formData.language !== 'English' && (
                <p className="text-[10px] text-slate-400 italic mt-2" data-testid="consent-translation-note">
                  Consent text shown in English. Full translation to {formData.language} available on request.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Verified-encounter opt-in — Truth-lock §9.9. Default OFF so demos,
            testing, and exploration never inflate the public real-encounter
            count. Hidden for validator submissions (never counted). */}
        {!isValidator && !localValidatorMode && (
          <div className="bg-emerald-50 rounded-xl p-4 mb-6 border border-emerald-200" data-testid="real-patient-consent-block">
            <div className="flex items-start gap-3">
              <Checkbox
                checked={formData.realPatientConsent}
                onCheckedChange={v => updateField('realPatientConsent', v)}
                data-testid="real-patient-consent-checkbox"
                className="mt-0.5"
              />
              <div className="text-sm text-slate-700">
                <p className="font-medium mb-1">This is a real patient encounter</p>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Check this only for genuine clinical use. Leave it unchecked for demos,
                  testing, or exploration. This MVP build uses synthetic tests only — no PHI.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Safety Notice */}
        <div className="flex items-start gap-2 mb-6 text-xs text-slate-500" data-testid="safety-notice">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <div>
            <p>Accessibility accommodations do not reduce clinical urgency.</p>
            <p className="font-semibold text-red-600 mt-1">If experiencing a medical emergency, call emergency services immediately.</p>
          </div>
        </div>

        {/* Founder Mode: Test Case Toggle */}
        {isFounder && (
          <div className="flex items-center gap-2 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl" data-testid="test-case-toggle">
            <Checkbox checked={isTestCase} onCheckedChange={setIsTestCase} />
            <span className="text-sm text-amber-800 font-medium">Mark as System Test (will not count as Real User)</span>
          </div>
        )}

        {/* Submit — Two Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button
            onClick={() => { handleSubmit('patient'); }}
            disabled={isProcessing || !formData.consentGiven}
            className="rounded-md py-4 bg-[#007BFF] hover:bg-blue-700 text-white font-medium text-base shadow-sm hover:shadow transition-all"
            data-testid="submit-patient-btn"
          >
            {isProcessing ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
            ) : (
              <><Send className="w-5 h-5 mr-2" /> {editMode ? 'Save & Rerun (Patient View)' : 'My Health Records Summary'}</>
            )}
          </Button>
          <Button
            onClick={() => {
              if (!formData.consentGiven) { alert('Please provide consent before submitting.'); return; }
              if (!formData.chiefComplaint.trim()) { alert('Please enter your chief complaint.'); return; }
              if (!(formData.allergies || '').trim()) { alert('Allergies is a required field. If you have none, please type "None known".'); return; }
              setShowPhysicianWelcome(true);
            }}
            disabled={isProcessing || !formData.consentGiven}
            className="rounded-md py-4 bg-[#28a745] hover:bg-green-700 text-white font-medium text-base shadow-sm hover:shadow transition-all"
            data-testid="submit-physician-btn"
          >
            {isProcessing ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
            ) : (
              <><Send className="w-5 h-5 mr-2" /> {editMode ? 'Save & Rerun (SOAP)' : 'Physician SOAP Notes'}</>
            )}
          </Button>
        </div>
      </div>
      </div>

      {/* Physician Welcome Modal — shown before SOAP processing begins */}
      <Dialog open={showPhysicianWelcome} onOpenChange={setShowPhysicianWelcome}>
        <DialogContent className="max-w-lg" data-testid="physician-welcome-dialog">
          <div className="p-2">
            <h3 className="text-lg font-bold text-slate-900 mb-3">Welcome, Dear Physician</h3>
            <p className="text-sm text-slate-700 leading-relaxed">
              Welcome Dear Physician, I appreciate your interest in this demo to validate the SOAP Notes which the AI generates deterministically. Please, after seeing the SOAP Notes, kindly give your feedback in the Feedback box below, and register your Name and Email so Sivam knows whose feedback this is from, to clarify and follow up personally.
            </p>
            <div className="mt-5 flex justify-end">
              <Button
                onClick={() => { setShowPhysicianWelcome(false); handleSubmit('physician'); }}
                className="rounded-md bg-[#28a745] hover:bg-green-700 text-white font-medium px-5"
                data-testid="physician-welcome-continue-btn"
              >
                View SOAP Notes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
